import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, ArrowLeftRight, TrendingUp, TrendingDown, Repeat,
  Landmark, Folder, PieChart, Target, CalendarClock, BellRing, RefreshCw,
  BarChart3, Sparkles, LineChart, UploadCloud, FileSpreadsheet, Settings,
  Plus, Search, Filter, AlertTriangle, CheckCircle2, X, Eye, EyeOff,
  Trash2, Edit, ChevronRight, Download, Upload, ShieldCheck, Zap,
  Send, Bot, User, IndianRupee, HelpCircle, Check, Copy, Tag,
  Clock, Calendar, FileText, ArrowUpRight, ArrowDownRight, RefreshCcw
} from 'lucide-react';
import {
  getExpenseDashboard, getExpenseInsights, getExpenseReports,
  listTransactions, createTransaction, updateTransaction, deleteTransaction, createTransfer,
  listAccounts, createAccount, updateAccount, deleteAccount,
  listCategories, createCategory, createSubcategory,
  listBudgets, createBudget, updateBudget, deleteBudget,
  listGoals, createGoal, updateGoal, deleteGoal,
  listSubscriptions, createSubscription, updateSubscription, deleteSubscription,
  listRecurring, createRecurring, updateRecurring, deleteRecurring,
  parseNaturalLanguage, parseImage, chatAiAssistant,
  getAiSettings, updateAiSettings, testAiKey, removeAiKey, confirmImport
} from '../../api/expenses';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/MainLayout';
import VoiceExpenseFab from '../../components/expenses/VoiceExpenseFab';

export default function ExpenseTrackerApp() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Active view derived from pathname
  const activeTab = useMemo(() => {
    const p = location.pathname.replace(/^\/apps\/(expense-tracker|expenses)/, '').replace(/^\//, '');
    return p || 'dashboard';
  }, [location.pathname]);

  const setTab = (tab) => {
    if (tab === 'dashboard') {
      navigate('/apps/expense-tracker');
    } else {
      navigate(`/apps/expense-tracker/${tab}`);
    }
  };

  // ─── CORE STATE ───
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [totalTxCount, setTotalTxCount] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [goals, setGoals] = useState([]);
  const [subscriptionsData, setSubscriptionsData] = useState({ subscriptions: [], monthlyCost: 0, annualCost: 0 });
  const [recurringRules, setRecurringRules] = useState([]);
  const [aiSettings, setAiSettings] = useState(null);
  const [insightsData, setInsightsData] = useState(null);
  const [toast, setToast] = useState(null);

  // ─── HERO ACTIONS TOOLTIP STATE (ON HOVER & CLICK) ───
  const [activeHeroTooltip, setActiveHeroTooltip] = useState(null);
  const heroTooltipTimeoutRef = useRef(null);

  const triggerHeroTooltip = (actionId) => {
    if (heroTooltipTimeoutRef.current) clearTimeout(heroTooltipTimeoutRef.current);
    setActiveHeroTooltip(actionId);
    heroTooltipTimeoutRef.current = setTimeout(() => {
      setActiveHeroTooltip(null);
    }, 2200);
  };

  const clearHeroTooltip = () => {
    if (heroTooltipTimeoutRef.current) clearTimeout(heroTooltipTimeoutRef.current);
    setActiveHeroTooltip(null);
  };

  // ─── MODAL STATES ───
  const [txModalOpen, setTxModalOpen] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [txFormType, setTxFormType] = useState('EXPENSE');
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [budgetModalOpen, setBudgetModalOpen] = useState(false);
  const [goalModalOpen, setGoalModalOpen] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [billModalOpen, setBillModalOpen] = useState(false);
  const [recurringModalOpen, setRecurringModalOpen] = useState(false);

  // ─── BILLS & REMINDERS PERSISTED STATE ───
  const [bills, setBills] = useState(() => {
    try {
      const saved = localStorage.getItem('pm_expense_bills');
      if (saved) return JSON.parse(saved);
    } catch {}
    return [];
  });

  const saveBillsToStorage = (updatedBills) => {
    setBills(updatedBills);
    try {
      localStorage.setItem('pm_expense_bills', JSON.stringify(updatedBills));
    } catch {}
  };

  // ─── AI EXTRACTION CONFIRMATION MODAL ───
  const [aiConfirmModalOpen, setAiConfirmModalOpen] = useState(false);
  const [aiCandidate, setAiCandidate] = useState(null);
  const [aiRisk, setAiRisk] = useState(null);
  const [aiDuplicate, setAiDuplicate] = useState(null);
  const [duplicateChoice, setDuplicateChoice] = useState('keep'); // 'keep', 'ignore'

  // ─── QUICK AI TEXT ENTRY ───
  const [nlText, setNlText] = useState('');
  const [nlParsing, setNlParsing] = useState(false);

  // ─── AI CHAT ASSISTANT ───
  const [chatMessages, setChatMessages] = useState([
    {
      role: 'assistant',
      text: 'Hello! I am your CipherVault Financial Assistant. Ask me anything about your spending, budgets, savings, or categories in English, Hindi, or Gujarati.',
    }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  // ─── TRANSACTION FILTERS ───
  const [txFilterType, setTxFilterType] = useState('ALL');
  const [txFilterCategory, setTxFilterCategory] = useState('');
  const [txFilterAccount, setTxFilterAccount] = useState('');
  const [txSearch, setTxSearch] = useState('');
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  const formatTxDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (_) {
      return dateStr;
    }
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ─── DATA FETCHING ───
  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, accRes, catRes, budRes, goalRes, subRes, recurRes, aiSetRes] = await Promise.all([
        getExpenseDashboard().catch(() => ({ data: null })),
        listAccounts().catch(() => ({ data: [] })),
        listCategories().catch(() => ({ data: [] })),
        listBudgets().catch(() => ({ data: [] })),
        listGoals().catch(() => ({ data: [] })),
        listSubscriptions().catch(() => ({ data: { subscriptions: [], monthlyCost: 0, annualCost: 0 } })),
        listRecurring().catch(() => ({ data: [] })),
        getAiSettings().catch(() => ({ data: null })),
      ]);

      setDashboardData(dashRes.data);
      setAccounts(accRes.data || []);
      setCategories(catRes.data || []);
      setBudgets(budRes.data || []);
      setGoals(goalRes.data || []);
      setSubscriptionsData(subRes.data || { subscriptions: [], monthlyCost: 0, annualCost: 0 });
      setRecurringRules(recurRes.data || []);
      setAiSettings(aiSetRes.data);
    } catch (err) {
      showToast('Error loading financial data', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTransactions = useCallback(async () => {
    try {
      const res = await listTransactions({
        type: txFilterType === 'ALL' ? undefined : txFilterType,
        category_id: txFilterCategory || undefined,
        account_id: txFilterAccount || undefined,
        search: txSearch || undefined,
        limit: 100,
      });
      setTransactions(res.data.transactions || []);
      setTotalTxCount(res.data.total || 0);
    } catch (err) {
      // ignore
    }
  }, [txFilterType, txFilterCategory, txFilterAccount, txSearch]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  useEffect(() => {
    if (['transactions', 'income', 'expenses', 'transfers'].includes(activeTab)) {
      loadTransactions();
    }
  }, [activeTab, loadTransactions]);

  useEffect(() => {
    if (activeTab === 'insights') {
      getExpenseInsights().then(res => setInsightsData(res.data)).catch(() => {});
    }
  }, [activeTab]);

  // ─── QUICK NATURAL LANGUAGE SUBMISSION ───
  const handleNlSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!nlText.trim()) return;

    setNlParsing(true);
    try {
      const res = await parseNaturalLanguage(nlText.trim());
      const { extracted, risk, duplicate } = res.data;
      setAiCandidate(extracted);
      setAiRisk(risk);
      setAiDuplicate(duplicate);
      setDuplicateChoice('keep');
      setAiConfirmModalOpen(true);
      setNlText('');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to parse expense with AI', 'error');
    } finally {
      setNlParsing(false);
    }
  };

  // ─── CONFIRM AI EXTRACTED EXPENSE ───
  const handleConfirmAiExpense = async () => {
    if (!aiCandidate) return;
    if (aiDuplicate?.is_duplicate && duplicateChoice === 'ignore') {
      setAiConfirmModalOpen(false);
      showToast('Duplicate transaction ignored', 'info');
      return;
    }

    try {
      const defaultAcc = accounts.find(a => a.is_default) || accounts[0];
      const today = new Date().toISOString().split('T')[0];

      const itemsToCreate = (aiCandidate.multiple_expenses && aiCandidate.multiple_expenses.length > 0)
        ? aiCandidate.multiple_expenses
        : [aiCandidate];

      for (const item of itemsToCreate) {
        let catId = null;
        if (item.category) {
          const found = categories.find(c => c.name.toLowerCase() === item.category.toLowerCase());
          if (found) catId = found.id;
        }
        if (!catId && categories.length > 0) {
          catId = categories.find(c => /food|grocer|vegetable/i.test(c.name))?.id ||
                  categories.find(c => c.type === 'EXPENSE' || c.type === 'BOTH')?.id ||
                  categories[0]?.id;
        }

        await createTransaction({
          type: item.type || 'EXPENSE',
          amount: Number(item.amount),
          currency: item.currency || 'INR',
          category_id: catId,
          account_id: defaultAcc?.id,
          merchant: item.merchant,
          description: item.description,
          transaction_date: item.transaction_date || today,
          payment_method: item.payment_method || 'Cash',
          source: 'AI_TEXT',
          reference_id: item.reference_id,
          confidence: item.confidence,
        });
      }

      if (itemsToCreate.length > 1) {
        showToast(`${itemsToCreate.length} separate expenses added totaling ₹${aiCandidate.amount}`);
      } else {
        showToast(`Expense added: ₹${aiCandidate.amount} (${aiCandidate.description})`);
      }

      setAiConfirmModalOpen(false);
      setAiCandidate(null);
      loadAllData();
      if (['transactions', 'income', 'expenses'].includes(activeTab)) {
        loadTransactions();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save transaction', 'error');
    }
  };

  // ─── AI CHAT ASSISTANT QUERY ───
  const handleSendChat = async (preset) => {
    const query = preset || chatInput;
    if (!query.trim()) return;

    const newMsgs = [...chatMessages, { role: 'user', text: query }];
    setChatMessages(newMsgs);
    if (!preset) setChatInput('');
    setChatLoading(true);

    try {
      const res = await chatAiAssistant(query);
      setChatMessages([...newMsgs, { role: 'assistant', text: res.data.response, tools: res.data.toolsUsed }]);
    } catch (err) {
      setChatMessages([...newMsgs, { role: 'assistant', text: 'Sorry, I could not process your request right now.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  // ─── MANUAL TRANSACTION FORM STATE ───
  const [txForm, setTxForm] = useState({
    type: 'EXPENSE',
    amount: '',
    category_id: '',
    subcategory_id: '',
    account_id: '',
    merchant: '',
    description: '',
    transaction_date: new Date().toISOString().split('T')[0],
    payment_method: 'UPI',
    notes: '',
  });

  const openAddTransaction = (type = 'EXPENSE') => {
    setEditingTx(null);
    setTxFormType(type);
    setTxForm({
      type,
      amount: '',
      category_id: categories.find(c => c.type === type || c.type === 'BOTH')?.id || categories[0]?.id || '',
      subcategory_id: '',
      account_id: accounts[0]?.id || '',
      merchant: '',
      description: '',
      transaction_date: new Date().toISOString().split('T')[0],
      payment_method: 'UPI',
      notes: '',
    });
    setTxModalOpen(true);
  };

  const handleSaveTransaction = async (e) => {
    e.preventDefault();
    if (!txForm.amount || Number(txForm.amount) <= 0) {
      showToast('Please enter a valid amount', 'error');
      return;
    }

    try {
      if (editingTx) {
        await updateTransaction(editingTx.id, {
          ...txForm,
          amount: Number(txForm.amount),
        });
        showToast('Transaction updated successfully');
      } else {
        await createTransaction({
          ...txForm,
          amount: Number(txForm.amount),
        });
        showToast('Transaction created successfully');
      }
      setTxModalOpen(false);
      loadAllData();
      loadTransactions();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save transaction', 'error');
    }
  };

  const handleDeleteTransaction = async (id) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) return;
    try {
      await deleteTransaction(id);
      showToast('Transaction deleted');
      loadAllData();
      loadTransactions();
    } catch (err) {
      showToast('Failed to delete transaction', 'error');
    }
  };

  // ─── TRANSFER FORM STATE ───
  const [transferForm, setTransferForm] = useState({
    from_account_id: '',
    to_account_id: '',
    amount: '',
    transaction_date: new Date().toISOString().split('T')[0],
    description: 'Account Transfer',
    notes: '',
  });

  const handleSaveTransfer = async (e) => {
    e.preventDefault();
    if (transferForm.from_account_id === transferForm.to_account_id) {
      showToast('Source and destination accounts must be different', 'error');
      return;
    }
    try {
      await createTransfer({
        ...transferForm,
        amount: Number(transferForm.amount),
      });
      showToast('Transfer completed successfully (Net expense: ₹0)');
      setTransferModalOpen(false);
      loadAllData();
      loadTransactions();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to complete transfer', 'error');
    }
  };

  // ─── BILLS & RECURRING HANDLERS ───
  const handleMarkBillPaid = async (bill) => {
    try {
      const updated = bills.map((b) => b.id === bill.id ? { ...b, status: 'PAID' } : b);
      saveBillsToStorage(updated);

      const targetCat = categories.find(c => c.name.toLowerCase().includes('bill') || c.name.toLowerCase().includes('util')) || categories[0];
      const targetAcc = accounts[0];

      await createTransaction({
        type: 'EXPENSE',
        amount: Number(bill.amount),
        currency: 'INR',
        category_id: targetCat?.id,
        account_id: targetAcc?.id,
        merchant: bill.name,
        description: `Bill Payment: ${bill.name}`,
        transaction_date: new Date().toISOString().split('T')[0],
        payment_method: 'UPI',
        source: 'MANUAL',
      });

      showToast(`Bill "${bill.name}" marked as paid & ₹${bill.amount} logged!`);
      loadAllData();
      loadTransactions();
    } catch (err) {
      showToast('Bill marked as paid');
    }
  };

  const handleDeleteBill = (id) => {
    const updated = bills.filter((b) => b.id !== id);
    saveBillsToStorage(updated);
    showToast('Bill reminder removed');
  };

  const handleDeleteBudget = async (id) => {
    if (!window.confirm('Delete this budget?')) return;
    try {
      await deleteBudget(id);
      showToast('Budget deleted');
      loadAllData();
    } catch (err) {
      showToast('Failed to delete budget', 'error');
    }
  };

  const handleQuickCreateBudget = async (name, amount, catName = '') => {
    try {
      const cat = catName ? categories.find(c => c.name.toLowerCase().includes(catName.toLowerCase())) : null;
      await createBudget({
        name,
        amount,
        category_id: cat?.id || null,
        threshold_percent: 80,
        period: 'MONTHLY',
      });
      showToast(`Budget "${name}" created!`);
      loadAllData();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to create budget', 'error');
    }
  };

  const handleDeleteRecurring = async (id) => {
    if (!window.confirm('Delete this recurring schedule?')) return;
    try {
      await deleteRecurring(id);
      showToast('Recurring schedule deleted');
      loadAllData();
    } catch (err) {
      showToast('Failed to delete recurring schedule', 'error');
    }
  };

  // ─── RENDER SUB-VIEWS ───

  return (
    <MainLayout>
      {/* Toast Notification */}
      {toast && (
        <div className={`et-toast et-toast-${toast.type}`}>
          {toast.type === 'error' ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />}
          <span>{toast.msg}</span>
        </div>
      )}

      {/* Header Banner & Quick Actions - Full Bleed Edge-to-Edge */}
      <div className="et-header notes-hero-banner">
        <div className="notes-hero-content">
          <div className="et-header-info">
            <div className="et-title-row">
              <div className="et-app-icon-wrap notes-hero-icon-badge">
                <Landmark size={24} color="#ffffff" />
              </div>
              <div className="et-title-text-group">
                <h1 className="et-app-title notes-hero-title">Expense Tracker & AI Assistant</h1>
                <p className="et-app-subtitle notes-hero-subtitle">Financial intelligence, budgets, receipts, & multilingual AI</p>
              </div>
            </div>
          </div>

          {/* Action Buttons with Icon-Only Mobile View and Hover/Click Tooltips */}
          <div className="et-header-actions">
            <div
              className={`et-hero-action-wrapper ${activeHeroTooltip === 'expense' ? 'show-tooltip' : ''}`}
              onMouseEnter={() => triggerHeroTooltip('expense')}
              onMouseLeave={clearHeroTooltip}
            >
              <button
                type="button"
                className="et-btn et-btn-primary notes-hero-btn-primary et-hero-action-btn"
                onClick={() => {
                  triggerHeroTooltip('expense');
                  openAddTransaction('EXPENSE');
                }}
                title="Add Expense"
                aria-label="Add Expense"
              >
                <Plus size={16} />
                <span className="et-btn-label">Add Expense</span>
              </button>
              <div className="et-hero-tooltip">Add Expense</div>
            </div>

            <div
              className={`et-hero-action-wrapper ${activeHeroTooltip === 'income' ? 'show-tooltip' : ''}`}
              onMouseEnter={() => triggerHeroTooltip('income')}
              onMouseLeave={clearHeroTooltip}
            >
              <button
                type="button"
                className="et-btn et-btn-secondary notes-hero-btn-secondary et-hero-action-btn"
                onClick={() => {
                  triggerHeroTooltip('income');
                  openAddTransaction('INCOME');
                }}
                title="Add Income"
                aria-label="Add Income"
              >
                <TrendingUp size={16} />
                <span className="et-btn-label">Add Income</span>
              </button>
              <div className="et-hero-tooltip">Add Income</div>
            </div>

            <div
              className={`et-hero-action-wrapper ${activeHeroTooltip === 'transfer' ? 'show-tooltip' : ''}`}
              onMouseEnter={() => triggerHeroTooltip('transfer')}
              onMouseLeave={clearHeroTooltip}
            >
              <button
                type="button"
                className="et-btn et-btn-secondary notes-hero-btn-secondary et-hero-action-btn"
                onClick={() => {
                  triggerHeroTooltip('transfer');
                  setTransferForm({
                    from_account_id: accounts[0]?.id || '',
                    to_account_id: accounts[1]?.id || '',
                    amount: '',
                    transaction_date: new Date().toISOString().split('T')[0],
                    description: 'Account Transfer',
                    notes: '',
                  });
                  setTransferModalOpen(true);
                }}
                title="Transfer Funds"
                aria-label="Transfer Funds"
              >
                <Repeat size={16} />
                <span className="et-btn-label">Transfer</span>
              </button>
              <div className="et-hero-tooltip">Transfer</div>
            </div>

            <div
              className={`et-hero-action-wrapper ${activeHeroTooltip === 'receipt' ? 'show-tooltip' : ''}`}
              onMouseEnter={() => triggerHeroTooltip('receipt')}
              onMouseLeave={clearHeroTooltip}
            >
              <button
                type="button"
                className="et-btn et-btn-ai notes-hero-btn-secondary et-hero-action-btn"
                onClick={() => {
                  triggerHeroTooltip('receipt');
                  setReceiptModalOpen(true);
                }}
                title="Scan Receipt with AI"
                aria-label="Scan Receipt with AI"
              >
                <Sparkles size={16} color="#fde047" />
                <span className="et-btn-label">Scan Receipt</span>
              </button>
              <div className="et-hero-tooltip">Scan Receipt</div>
            </div>

            <div
              className={`et-hero-action-wrapper ${activeHeroTooltip === 'ai' ? 'show-tooltip' : ''}`}
              onMouseEnter={() => triggerHeroTooltip('ai')}
              onMouseLeave={clearHeroTooltip}
            >
              <button
                type="button"
                className="et-btn et-btn-outline notes-hero-btn-secondary et-hero-action-btn"
                onClick={() => {
                  triggerHeroTooltip('ai');
                  setTab('ai');
                }}
                title="Ask AI Assistant"
                aria-label="Ask AI Assistant"
              >
                <Bot size={16} />
                <span className="et-btn-label">Ask AI</span>
              </button>
              <div className="et-hero-tooltip">Ask AI</div>
            </div>
          </div>
        </div>
      </div>

      <div className="et-app-container animate-fade-in">

      {/* Quick Natural Language AI Bar */}
      <div className="et-nl-bar-card">
        <div className="et-nl-icon"><Sparkles size={18} color="#059669" /></div>
        <form onSubmit={handleNlSubmit} className="et-nl-form">
          <input
            type="text"
            className="et-nl-input"
            placeholder='Quick Add or Ask AI: "10 rupay samosa", "500 petrol", "આજે 300 રિક્ષા"...'
            value={nlText}
            onChange={(e) => setNlText(e.target.value)}
            disabled={nlParsing}
          />
          <button type="submit" className="et-nl-submit-btn" disabled={nlParsing || !nlText.trim()}>
            {nlParsing ? <RefreshCcw size={14} className="et-spin" /> : <Send size={14} />}
            <span>{nlParsing ? 'Analyzing...' : 'Parse'}</span>
          </button>
        </form>
      </div>

      {/* Main Tab Navigation */}
      <div className="et-nav-tabs-wrapper">
        <div className="et-nav-tabs">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
            { id: 'transactions', label: 'Transactions', icon: ArrowLeftRight },
            { id: 'accounts', label: 'Accounts', icon: Landmark },
            { id: 'categories', label: 'Categories', icon: Folder },
            { id: 'budgets', label: 'Budgets', icon: PieChart },
            { id: 'goals', label: 'Goals', icon: Target },
            { id: 'subscriptions', label: 'Subscriptions', icon: CalendarClock },
            { id: 'bills', label: 'Bills & Reminders', icon: BellRing },
            { id: 'recurring', label: 'Recurring', icon: RefreshCw },
            { id: 'analytics', label: 'Analytics', icon: BarChart3 },
            { id: 'ai', label: 'AI Financial Assistant', icon: Sparkles },
            { id: 'insights', label: 'Spending Insights', icon: LineChart },
            { id: 'import', label: 'Import', icon: UploadCloud },
            { id: 'reports', label: 'Reports', icon: FileSpreadsheet },
            { id: 'settings', label: 'Settings', icon: Settings },
          ].map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                className={`et-tab-btn ${active ? 'et-tab-btn-active' : ''}`}
                onClick={() => setTab(tab.id)}
              >
                <Icon size={16} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Subview Content */}
      <div className="et-view-content">
        {/* 1. DASHBOARD VIEW */}
        {activeTab === 'dashboard' && (
          <div className="et-dashboard-grid">
            {/* Stat Cards Row */}
            <div className="et-stats-row">
              <div className="et-stat-card et-stat-balance">
                <div className="et-stat-header">
                  <span className="et-stat-label">Current Balance</span>
                  <div className="et-stat-icon-wrap"><Landmark size={18} /></div>
                </div>
                <div className="et-stat-val">₹{(dashboardData?.metrics?.currentBalance || 0).toLocaleString('en-IN')}</div>
                <span className="et-stat-sub">Across {accounts.length} accounts</span>
              </div>

              <div className="et-stat-card et-stat-income">
                <div className="et-stat-header">
                  <span className="et-stat-label">Total Income (This Month)</span>
                  <div className="et-stat-icon-wrap"><ArrowUpRight size={18} /></div>
                </div>
                <div className="et-stat-val">₹{(dashboardData?.metrics?.totalIncome || 0).toLocaleString('en-IN')}</div>
                <span className="et-stat-sub">Active revenue streams</span>
              </div>

              <div className="et-stat-card et-stat-expense">
                <div className="et-stat-header">
                  <span className="et-stat-label">Total Expenses (This Month)</span>
                  <div className="et-stat-icon-wrap"><ArrowDownRight size={18} /></div>
                </div>
                <div className="et-stat-val">₹{(dashboardData?.metrics?.totalExpenses || 0).toLocaleString('en-IN')}</div>
                <span className="et-stat-sub">Transfers strictly excluded</span>
              </div>

              <div className="et-stat-card et-stat-savings">
                <div className="et-stat-header">
                  <span className="et-stat-label">Net Savings & Rate</span>
                  <div className="et-stat-icon-wrap"><TrendingUp size={18} /></div>
                </div>
                <div className="et-stat-val">
                  ₹{(dashboardData?.metrics?.netSavings || 0).toLocaleString('en-IN')}
                </div>
                <span className="et-stat-sub">
                  Savings rate: <strong>{dashboardData?.metrics?.savingsRate || 0}%</strong>
                </span>
              </div>

              <div className="et-stat-card et-stat-budget">
                <div className="et-stat-header">
                  <span className="et-stat-label">Budget Usage</span>
                  <div className="et-stat-icon-wrap"><PieChart size={18} /></div>
                </div>
                <div className="et-stat-val">{dashboardData?.metrics?.budgetUsage || 0}%</div>
                <div className="et-progress-bar-bg">
                  <div
                    className="et-progress-bar-fill"
                    style={{
                      width: `${Math.min(100, dashboardData?.metrics?.budgetUsage || 0)}%`,
                      backgroundColor: (dashboardData?.metrics?.budgetUsage || 0) > 90 ? '#ef4444' : '#10b981',
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Charts & Insights Row */}
            <div className="et-dashboard-charts-grid">
              {/* Monthly Trend Chart */}
              <div className="et-card et-chart-card">
                <h3 className="et-card-title">Income vs Expense Trend (Last 6 Months)</h3>
                <div className="et-bar-chart-container">
                  {(dashboardData?.charts?.monthlyTrend || []).map((m, idx) => {
                    const maxVal = Math.max(...(dashboardData?.charts?.monthlyTrend.map(x => Math.max(x.income, x.expense)) || [1]));
                    const incH = maxVal > 0 ? (m.income / maxVal) * 120 : 0;
                    const expH = maxVal > 0 ? (m.expense / maxVal) * 120 : 0;
                    return (
                      <div key={idx} className="et-bar-group">
                        <div className="et-bars-pair">
                          <div className="et-bar et-bar-inc" style={{ height: `${incH}px` }} title={`Income: ₹${m.income}`} />
                          <div className="et-bar et-bar-exp" style={{ height: `${expH}px` }} title={`Expense: ₹${m.expense}`} />
                        </div>
                        <span className="et-bar-label">{m.month}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="et-chart-legend">
                  <span className="et-legend-item"><span className="et-legend-color et-bg-income" /> Income</span>
                  <span className="et-legend-item"><span className="et-legend-color et-bg-expense" /> Expense</span>
                </div>
              </div>

              {/* Category Spending Breakdown */}
              <div className="et-card et-chart-card">
                <h3 className="et-card-title">Category Spending Breakdown</h3>
                <div className="et-cat-breakdown-list">
                  {(dashboardData?.charts?.categorySpending || []).length === 0 ? (
                    <div className="et-empty-state">No category expenses recorded this month yet.</div>
                  ) : (
                    (dashboardData?.charts?.categorySpending || []).map((cat, idx) => {
                      const totalExp = dashboardData?.metrics?.totalExpenses || 1;
                      const pct = Math.round((cat.amount / totalExp) * 100);
                      return (
                        <div key={idx} className="et-cat-row">
                          <div className="et-cat-info">
                            <span className="et-cat-dot" style={{ backgroundColor: cat.color }} />
                            <span className="et-cat-name">{cat.category}</span>
                            <span className="et-cat-amt">₹{cat.amount.toLocaleString('en-IN')}</span>
                            <span className="et-cat-pct">({pct}%)</span>
                          </div>
                          <div className="et-progress-bar-bg">
                            <div className="et-progress-bar-fill" style={{ width: `${pct}%`, backgroundColor: cat.color }} />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Recent Transactions & Alerts Row */}
            <div className="et-dashboard-bottom-grid">
              {/* Recent Transactions */}
              <div className="et-card">
                <div className="et-card-header-row">
                  <h3 className="et-card-title">Recent Transactions</h3>
                  <button className="et-link-btn" onClick={() => setTab('transactions')}>View All ({totalTxCount})</button>
                </div>
                <div className="et-tx-mini-list">
                  {(dashboardData?.recentTransactions || []).length === 0 ? (
                    <div className="et-empty-state">No transactions yet. Click "+ Add Expense" or use the quick bar above!</div>
                  ) : (
                    dashboardData?.recentTransactions.map((tx) => (
                      <div key={tx.id} className="et-tx-item">
                        <div className="et-tx-left">
                          <div className={`et-tx-icon-wrap et-tx-type-${tx.type.toLowerCase()}`}>
                            {tx.type === 'INCOME' ? <TrendingUp size={16} /> : tx.type === 'TRANSFER' ? <Repeat size={16} /> : <TrendingDown size={16} />}
                          </div>
                          <div>
                            <div className="et-tx-desc">{tx.description || tx.merchant || 'Expense'}</div>
                            <div className="et-tx-meta">
                              {tx.transaction_date} • {tx.category_name || 'Other'} • {tx.account_name || 'Cash'}
                            </div>
                          </div>
                        </div>
                        <div className="et-tx-right">
                          <span className={`et-tx-amount et-tx-amt-${tx.type.toLowerCase()}`}>
                            {tx.type === 'INCOME' ? '+' : tx.type === 'EXPENSE' ? '-' : ''}₹{Number(tx.amount).toLocaleString('en-IN')}
                          </span>
                          <span className="et-source-badge">{tx.source}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Upcoming Bills & Budgets Alerts */}
              <div className="et-card">
                <h3 className="et-card-title">Upcoming Bills & Subscriptions</h3>
                <div className="et-bills-list">
                  {(dashboardData?.upcomingBills || []).length === 0 ? (
                    <div className="et-empty-state">No upcoming bills due soon.</div>
                  ) : (
                    dashboardData?.upcomingBills.map((b) => (
                      <div key={b.id} className="et-bill-item">
                        <div className="et-bill-left">
                          <CalendarClock size={16} color="#3b82f6" />
                          <div>
                            <div className="et-bill-name">{b.name}</div>
                            <div className="et-bill-date">Next: {b.next_payment_date} ({b.billing_cycle})</div>
                          </div>
                        </div>
                        <div className="et-bill-amount">₹{Number(b.amount).toLocaleString('en-IN')}</div>
                      </div>
                    ))
                  )}
                </div>

                {/* Unusual Spending & Budget Alerts */}
                {dashboardData?.alerts && dashboardData.alerts.length > 0 && (
                  <div className="et-alerts-box">
                    <h4 className="et-alerts-box-title"><AlertTriangle size={15} color="#f59e0b" /> Active Financial Advisories</h4>
                    {dashboardData.alerts.map((al) => (
                      <div key={al.id} className={`et-alert-row et-alert-sev-${al.severity?.toLowerCase()}`}>
                        <strong>{al.title}:</strong> {al.message}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 2. TRANSACTIONS VIEW */}
        {(['transactions', 'income', 'expenses', 'transfers'].includes(activeTab)) && (
          <div className="et-card et-tx-table-card">
            {/* Desktop Filter Bar (> 640px) */}
            <div className="expense-desktop-filter-bar et-tx-filters-bar">
              <div className="et-search-wrap">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search description, merchant, category..."
                  value={txSearch}
                  onChange={(e) => setTxSearch(e.target.value)}
                  className="et-search-input"
                />
                {txSearch && (
                  <button type="button" className="notes-search-clear" onClick={() => setTxSearch('')}>&times;</button>
                )}
              </div>

              <div className="et-filters-row">
                <select
                  value={activeTab === 'income' ? 'INCOME' : activeTab === 'expenses' ? 'EXPENSE' : activeTab === 'transfers' ? 'TRANSFER' : txFilterType}
                  onChange={(e) => setTxFilterType(e.target.value)}
                  className="et-select"
                  disabled={activeTab !== 'transactions'}
                >
                  <option value="ALL">All Types</option>
                  <option value="EXPENSE">Expense (Debit)</option>
                  <option value="INCOME">Income (Credit)</option>
                  <option value="TRANSFER">Transfer (Net ₹0)</option>
                  <option value="REFUND">Refund</option>
                  <option value="INVESTMENT">Investment</option>
                </select>

                <select
                  value={txFilterCategory}
                  onChange={(e) => setTxFilterCategory(e.target.value)}
                  className="et-select"
                >
                  <option value="">All Categories</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>

                <select
                  value={txFilterAccount}
                  onChange={(e) => setTxFilterAccount(e.target.value)}
                  className="et-select"
                >
                  <option value="">All Accounts</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Mobile Filter Bar (<= 640px) - Single Row Icon-Only Bar */}
            <div className="expense-mobile-filter-bar tasks-mobile-filter-bar">
              {/* Search Toggle Icon */}
              <button
                type="button"
                onClick={() => setIsMobileSearchOpen(!isMobileSearchOpen)}
                className={`mobile-filter-icon-btn ${isMobileSearchOpen || txSearch ? 'active' : ''}`}
                title="Search transactions"
                aria-label="Toggle search input"
              >
                <Search size={16} />
              </button>

              {/* Type Filter Icon with native Select overlay */}
              <div
                className={`mobile-filter-select-wrapper ${txFilterType !== 'ALL' ? 'active' : ''}`}
                title={`Type: ${txFilterType}`}
              >
                {txFilterType === 'INCOME' ? <TrendingUp size={16} /> : txFilterType === 'TRANSFER' ? <Repeat size={16} /> : <TrendingDown size={16} />}
                <select
                  value={activeTab === 'income' ? 'INCOME' : activeTab === 'expenses' ? 'EXPENSE' : activeTab === 'transfers' ? 'TRANSFER' : txFilterType}
                  onChange={(e) => setTxFilterType(e.target.value)}
                  disabled={activeTab !== 'transactions'}
                  aria-label="Filter by type"
                >
                  <option value="ALL">All Types</option>
                  <option value="EXPENSE">Expense (Debit)</option>
                  <option value="INCOME">Income (Credit)</option>
                  <option value="TRANSFER">Transfer (Net ₹0)</option>
                  <option value="REFUND">Refund</option>
                  <option value="INVESTMENT">Investment</option>
                </select>
              </div>

              {/* Category Filter Icon with native Select overlay */}
              <div
                className={`mobile-filter-select-wrapper ${txFilterCategory ? 'active' : ''}`}
                title="Filter by category"
              >
                <Folder size={16} />
                <select
                  value={txFilterCategory}
                  onChange={(e) => setTxFilterCategory(e.target.value)}
                  aria-label="Filter by category"
                >
                  <option value="">All Categories</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              {/* Account Filter Icon with native Select overlay */}
              <div
                className={`mobile-filter-select-wrapper ${txFilterAccount ? 'active' : ''}`}
                title="Filter by account"
              >
                <Landmark size={16} />
                <select
                  value={txFilterAccount}
                  onChange={(e) => setTxFilterAccount(e.target.value)}
                  aria-label="Filter by account"
                >
                  <option value="">All Accounts</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>

              {/* Reset Filters Icon Button (Shown when active) */}
              {(txSearch || (txFilterType !== 'ALL' && activeTab === 'transactions') || txFilterCategory || txFilterAccount) && (
                <button
                  type="button"
                  onClick={() => {
                    setTxSearch('');
                    setTxFilterType('ALL');
                    setTxFilterCategory('');
                    setTxFilterAccount('');
                    setIsMobileSearchOpen(false);
                  }}
                  className="mobile-filter-icon-btn reset-btn"
                  title="Reset all filters"
                  aria-label="Reset filters"
                >
                  <X size={15} />
                </button>
              )}

              {/* Refresh Icon Button */}
              <button
                type="button"
                onClick={loadTransactions}
                className="mobile-filter-icon-btn"
                title="Refresh transactions"
                aria-label="Refresh transactions"
              >
                <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>

            {/* Expandable Mobile Search Bar */}
            {isMobileSearchOpen && (
              <div className="expense-mobile-search-expand tasks-mobile-search-expand animate-slide-down">
                <Search size={15} color="var(--text-secondary)" style={{ flexShrink: 0 }} />
                <input
                  type="text"
                  placeholder="Search description, merchant, category..."
                  value={txSearch}
                  onChange={(e) => setTxSearch(e.target.value)}
                  autoFocus
                />
                {txSearch && (
                  <button
                    type="button"
                    onClick={() => setTxSearch('')}
                    className="clear-search-btn"
                    title="Clear search"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            )}

            {/* Desktop Table View (> 640px) */}
            <div className="et-table-responsive et-desktop-tx-table">
              <table className="et-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description / Merchant</th>
                    <th>Type</th>
                    <th>Category</th>
                    <th>Amount</th>
                    <th>Account</th>
                    <th>Method</th>
                    <th>Source</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ textAlign: 'center', padding: '36px' }}>
                        No transactions found. Click "+ Add Expense" or use the natural language bar to create one.
                      </td>
                    </tr>
                  ) : (
                    transactions.map((tx) => (
                      <tr key={tx.id}>
                        <td>{formatTxDate(tx.transaction_date)}</td>
                        <td>
                          <strong>{tx.description || tx.merchant || 'Expense'}</strong>
                          {tx.merchant && tx.description !== tx.merchant && (
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{tx.merchant}</div>
                          )}
                        </td>
                        <td>
                          <span className={`et-badge et-badge-${tx.type.toLowerCase()}`}>
                            {tx.type}
                          </span>
                        </td>
                        <td>
                          <span className="et-cat-pill" style={{ borderColor: tx.category_color || '#10b981' }}>
                            {tx.category_name || 'Other'}
                          </span>
                        </td>
                        <td className={`et-tx-amt-${tx.type.toLowerCase()}`} style={{ fontWeight: '600' }}>
                          {tx.type === 'INCOME' ? '+' : tx.type === 'EXPENSE' ? '-' : ''}₹{Number(tx.amount).toLocaleString('en-IN')}
                        </td>
                        <td>{tx.account_name || 'Cash'}</td>
                        <td>{tx.payment_method || 'UPI'}</td>
                        <td>
                          <span className="et-source-tag">{tx.source}</span>
                        </td>
                        <td>
                          <div className="et-row-actions">
                            <button
                              className="et-icon-action-btn"
                              title="Edit"
                              onClick={() => {
                                setEditingTx(tx);
                                setTxForm({
                                  type: tx.type,
                                  amount: String(tx.amount),
                                  category_id: tx.category_id || '',
                                  subcategory_id: tx.subcategory_id || '',
                                  account_id: tx.account_id || '',
                                  merchant: tx.merchant || '',
                                  description: tx.description || '',
                                  transaction_date: tx.transaction_date,
                                  payment_method: tx.payment_method || 'UPI',
                                  notes: tx.notes || '',
                                });
                                setTxModalOpen(true);
                              }}
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              className="et-icon-action-btn et-btn-danger"
                              title="Delete"
                              onClick={() => handleDeleteTransaction(tx.id)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card List (<= 640px) */}
            <div className="et-mobile-tx-list">
              {transactions.length === 0 ? (
                <div className="et-empty-state" style={{ padding: '24px 16px' }}>
                  No transactions found. Click "+ Add Expense" or use the natural language bar to create one.
                </div>
              ) : (
                transactions.map((tx) => (
                  <div key={tx.id} className="et-mobile-tx-card glass-panel">
                    <div className="et-mobile-tx-card-left">
                      <div className={`et-tx-icon-wrap et-tx-type-${tx.type.toLowerCase()}`}>
                        {tx.type === 'INCOME' ? <TrendingUp size={15} /> : tx.type === 'TRANSFER' ? <Repeat size={15} /> : <TrendingDown size={15} />}
                      </div>
                      <div className="et-mobile-tx-card-details">
                        <div className="et-mobile-tx-title">
                          {tx.description || tx.merchant || 'Expense'}
                        </div>
                        <div className="et-mobile-tx-sub">
                          <span>{formatTxDate(tx.transaction_date)}</span>
                          <span>•</span>
                          <span className="et-cat-pill-mini" style={{ borderColor: tx.category_color || '#10b981' }}>
                            {tx.category_name || 'Other'}
                          </span>
                          <span>•</span>
                          <span>{tx.account_name || 'Cash'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="et-mobile-tx-card-right">
                      <div className={`et-tx-amount et-tx-amt-${tx.type.toLowerCase()}`}>
                        {tx.type === 'INCOME' ? '+' : tx.type === 'EXPENSE' ? '-' : ''}₹{Number(tx.amount).toLocaleString('en-IN')}
                      </div>
                      <div className="et-mobile-tx-actions">
                        <button
                          type="button"
                          className="btn-icon"
                          title="Edit"
                          onClick={() => {
                            setEditingTx(tx);
                            setTxForm({
                              type: tx.type,
                              amount: String(tx.amount),
                              category_id: tx.category_id || '',
                              subcategory_id: tx.subcategory_id || '',
                              account_id: tx.account_id || '',
                              merchant: tx.merchant || '',
                              description: tx.description || '',
                              transaction_date: tx.transaction_date,
                              payment_method: tx.payment_method || 'UPI',
                              notes: tx.notes || '',
                            });
                            setTxModalOpen(true);
                          }}
                        >
                          <Edit size={13} />
                        </button>
                        <button
                          type="button"
                          className="btn-icon et-btn-danger"
                          title="Delete"
                          onClick={() => handleDeleteTransaction(tx.id)}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* 6. ACCOUNTS VIEW */}
        {activeTab === 'accounts' && (
          <div className="et-accounts-page">
            <div className="et-card-header-row">
              <h2>Payment Accounts & Wallets</h2>
              <button className="et-btn et-btn-primary" onClick={() => setAccountModalOpen(true)}>
                <Plus size={16} /> Add Account
              </button>
            </div>
            <div className="et-accounts-grid">
              {accounts.map((acc) => (
                <div key={acc.id} className="et-acc-card" style={{ borderTop: `4px solid ${acc.color || '#10b981'}` }}>
                  <div className="et-acc-head">
                    <div>
                      <h3 className="et-acc-name">{acc.name}</h3>
                      <span className="et-acc-type">{acc.type} {acc.account_number_last4 ? `(•••${acc.account_number_last4})` : ''}</span>
                    </div>
                    {acc.is_default && <span className="et-badge et-badge-default">Default</span>}
                  </div>
                  <div className="et-acc-balance">
                    <span className="et-bal-label">Current Balance</span>
                    <span className="et-bal-val">₹{Number(acc.current_balance).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="et-acc-footer">
                    <span>Opening: ₹{Number(acc.opening_balance).toLocaleString('en-IN')}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 7. CATEGORIES VIEW */}
        {activeTab === 'categories' && (
          <div className="et-categories-page">
            <div className="et-card-header-row">
              <h2>Spending Categories & Subcategories</h2>
              <button className="et-btn et-btn-primary" onClick={() => setCategoryModalOpen(true)}>
                <Plus size={16} /> Add Custom Category
              </button>
            </div>
            <div className="et-categories-grid">
              {categories.map((cat) => (
                <div key={cat.id} className="et-cat-box" style={{ borderLeft: `4px solid ${cat.color || '#10b981'}` }}>
                  <div className="et-cat-box-header">
                    <h3 className="et-cat-box-name">{cat.name}</h3>
                    <span className="et-badge">{cat.type}</span>
                  </div>
                  <div className="et-subcat-tags">
                    {(cat.subcategories || []).map((sub) => (
                      <span key={sub.id} className="et-subcat-chip">{sub.name}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 8. BUDGETS VIEW */}
        {activeTab === 'budgets' && (
          <div className="et-budgets-page">
            <div className="et-card-header-row">
              <div>
                <h2>Budget Management & Threshold Alerts</h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.84rem', margin: '2px 0 0 0' }}>
                  Set spending ceilings with automated threshold alerts before overspending.
                </p>
              </div>
              <button className="et-btn et-btn-primary" onClick={() => setBudgetModalOpen(true)}>
                <Plus size={16} /> Create Budget
              </button>
            </div>

            {budgets.length === 0 ? (
              <div className="et-budget-guide-card glass-panel animate-fade-in">
                <div className="et-budget-guide-icon-bubble">
                  <PieChart size={32} color="#9333ea" />
                </div>
                <h3 className="et-guide-title">How to Set Up & Manage Budgets</h3>
                <p className="et-guide-desc">
                  Budgets let you enforce spending limits on individual categories (like Food or Fuel) or across your entire monthly cash flow.
                </p>

                <div className="et-guide-fields-grid">
                  <div className="et-guide-field-item">
                    <div className="et-guide-step-num">1</div>
                    <div className="et-guide-field-content">
                      <strong>Budget Name</strong>
                      <span>A descriptive name (e.g., "Monthly Dining & Groceries", "Fuel Limit")</span>
                    </div>
                  </div>
                  <div className="et-guide-field-item">
                    <div className="et-guide-step-num">2</div>
                    <div className="et-guide-field-content">
                      <strong>Category</strong>
                      <span>Link to a specific category, or leave as "Overall" to monitor all monthly spend</span>
                    </div>
                  </div>
                  <div className="et-guide-field-item">
                    <div className="et-guide-step-num">3</div>
                    <div className="et-guide-field-content">
                      <strong>Monthly Limit (₹)</strong>
                      <span>Maximum planned expense ceiling for the monthly cycle</span>
                    </div>
                  </div>
                  <div className="et-guide-field-item">
                    <div className="et-guide-step-num">4</div>
                    <div className="et-guide-field-content">
                      <strong>Alert Threshold (%)</strong>
                      <span>Triggers warnings at 80% or 90% before you reach your limit</span>
                    </div>
                  </div>
                </div>

                <div className="et-guide-presets-section">
                  <span className="et-presets-label">⚡ One-Tap Budget Quick Presets:</span>
                  <div className="et-presets-chips">
                    <button
                      type="button"
                      className="et-preset-chip"
                      onClick={() => handleQuickCreateBudget('Food & Dining Limit', 6000, 'Food')}
                    >
                      <Plus size={13} /> ₹6,000 Food Budget
                    </button>
                    <button
                      type="button"
                      className="et-preset-chip"
                      onClick={() => handleQuickCreateBudget('Fuel & Transport Limit', 2500, 'Transport')}
                    >
                      <Plus size={13} /> ₹2,500 Fuel & Transport
                    </button>
                    <button
                      type="button"
                      className="et-preset-chip"
                      onClick={() => handleQuickCreateBudget('Overall Monthly Budget', 25000, '')}
                    >
                      <Plus size={13} /> ₹25,000 Total Monthly Cap
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  className="et-btn et-btn-primary et-create-budget-btn"
                  onClick={() => setBudgetModalOpen(true)}
                >
                  <Plus size={16} /> Create Custom Budget
                </button>
              </div>
            ) : (
              <div className="et-budgets-grid">
                {budgets.map((b) => (
                  <div key={b.id} className="et-budget-card glass-panel">
                    <div className="et-budget-head">
                      <div>
                        <h3 className="et-budget-name">{b.name}</h3>
                        <span className="et-budget-sub">{b.period || 'MONTHLY'} • {b.category_name || 'Overall Total'}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {b.isExceeded ? (
                          <span className="et-badge et-badge-danger">Exceeded</span>
                        ) : (
                          <span className={`et-badge ${b.percentUsed >= (b.threshold_percent || 80) ? 'et-badge-warning' : 'et-badge-success'}`}>
                            {b.percentUsed || 0}% Used
                          </span>
                        )}
                        <button
                          type="button"
                          className="btn-icon et-btn-danger"
                          title="Delete Budget"
                          onClick={() => handleDeleteBudget(b.id)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="et-budget-figures">
                      <div>Spent: <strong>₹{Number(b.currentSpent || 0).toLocaleString('en-IN')}</strong></div>
                      <div>Limit: <strong>₹{Number(b.amount).toLocaleString('en-IN')}</strong></div>
                    </div>
                    <div className="et-progress-bar-bg">
                      <div
                        className="et-progress-bar-fill"
                        style={{
                          width: `${Math.min(100, b.percentUsed || 0)}%`,
                          backgroundColor: b.percentUsed >= 100 ? '#ef4444' : b.percentUsed >= (b.threshold_percent || 80) ? '#f59e0b' : '#10b981',
                        }}
                      />
                    </div>
                    <div className="et-budget-remaining">
                      <span>Remaining: <strong>₹{Number(b.remaining || 0).toLocaleString('en-IN')}</strong></span>
                      <span className="et-threshold-note">Alert at {b.threshold_percent || 80}%</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 9. GOALS VIEW */}
        {activeTab === 'goals' && (
          <div className="et-goals-page">
            <div className="et-card-header-row">
              <h2>Savings Goals & Target Projections</h2>
              <button className="et-btn et-btn-primary" onClick={() => setGoalModalOpen(true)}>
                <Plus size={16} /> Create Savings Goal
              </button>
            </div>
            <div className="et-goals-grid">
              {goals.map((g) => {
                const pct = Math.min(100, Math.round((Number(g.current_amount) / Number(g.target_amount)) * 100));
                return (
                  <div key={g.id} className="et-goal-card">
                    <div className="et-goal-head">
                      <h3>{g.name}</h3>
                      <span className="et-badge et-badge-info">{pct}%</span>
                    </div>
                    <div className="et-goal-amounts">
                      <div>Current: <strong>₹{Number(g.current_amount).toLocaleString('en-IN')}</strong></div>
                      <div>Target: <strong>₹{Number(g.target_amount).toLocaleString('en-IN')}</strong></div>
                    </div>
                    <div className="et-progress-bar-bg">
                      <div className="et-progress-bar-fill" style={{ width: `${pct}%`, backgroundColor: g.color || '#3b82f6' }} />
                    </div>
                    {g.target_date && <div className="et-goal-date">Target Date: {g.target_date}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 10. SUBSCRIPTIONS VIEW */}
        {activeTab === 'subscriptions' && (
          <div className="et-subs-page">
            <div className="et-card-header-row">
              <div>
                <h2>Recurring Subscriptions</h2>
                <p style={{ color: 'var(--text-secondary)' }}>
                  Estimated Monthly Cost: <strong>₹{subscriptionsData.monthlyCost.toLocaleString('en-IN')}</strong> • Annual Cost: <strong>₹{subscriptionsData.annualCost.toLocaleString('en-IN')}</strong>
                </p>
              </div>
              <button className="et-btn et-btn-primary" onClick={() => setSubscriptionModalOpen(true)}>
                <Plus size={16} /> Add Subscription
              </button>
            </div>
            <div className="et-subs-grid">
              {(subscriptionsData.subscriptions || []).map((sub) => (
                <div key={sub.id} className="et-sub-card">
                  <div className="et-sub-header">
                    <h3>{sub.name}</h3>
                    <span className="et-badge et-badge-success">{sub.billing_cycle}</span>
                  </div>
                  <div className="et-sub-cost">₹{Number(sub.amount).toLocaleString('en-IN')}</div>
                  <div className="et-sub-date">Next Billing Date: {sub.next_payment_date || 'N/A'}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 11. BILLS & REMINDERS VIEW */}
        {activeTab === 'bills' && (
          <div className="et-bills-page animate-fade-in">
            <div className="et-card-header-row">
              <div>
                <h2>Bills & Payment Reminders</h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.84rem', margin: '2px 0 0 0' }}>
                  Track upcoming utility bills, rent, and card dues. One-tap pay logs the expense automatically.
                </p>
              </div>
              <button className="et-btn et-btn-primary" onClick={() => setBillModalOpen(true)}>
                <Plus size={16} /> Add Bill Reminder
              </button>
            </div>

            {/* Bill Summary Metrics */}
            <div className="et-bill-metrics-grid">
              <div className="et-bill-metric-card glass-panel">
                <span className="et-bill-metric-label">Total Upcoming Dues</span>
                <div className="et-bill-metric-val" style={{ color: '#f59e0b' }}>
                  ₹{bills.filter(b => b.status === 'UPCOMING').reduce((acc, b) => acc + Number(b.amount || 0), 0).toLocaleString('en-IN')}
                </div>
                <span className="et-bill-metric-sub">{bills.filter(b => b.status === 'UPCOMING').length} pending bills</span>
              </div>
              <div className="et-bill-metric-card glass-panel">
                <span className="et-bill-metric-label">Paid This Cycle</span>
                <div className="et-bill-metric-val" style={{ color: '#10b981' }}>
                  ₹{bills.filter(b => b.status === 'PAID').reduce((acc, b) => acc + Number(b.amount || 0), 0).toLocaleString('en-IN')}
                </div>
                <span className="et-bill-metric-sub">{bills.filter(b => b.status === 'PAID').length} bills cleared</span>
              </div>
              <div className="et-bill-metric-card glass-panel">
                <span className="et-bill-metric-label">Scheduled Reminders</span>
                <div className="et-bill-metric-val" style={{ color: '#38bdf8' }}>
                  {bills.length}
                </div>
                <span className="et-bill-metric-sub">Active recurring alerts</span>
              </div>
            </div>

            {/* Bills List */}
            {bills.length === 0 ? (
              <div className="et-empty-state glass-panel" style={{ padding: '36px 20px', textAlign: 'center' }}>
                <BellRing size={36} color="#94a3b8" style={{ marginBottom: '10px' }} />
                <h3>No Bill Reminders Set</h3>
                <p style={{ color: 'var(--text-secondary)', maxWidth: '380px', margin: '6px auto 16px' }}>
                  Stay on top of electricity, wifi, credit cards, and rent. You'll receive due-date notifications before penalties occur.
                </p>
                <button className="et-btn et-btn-primary" onClick={() => setBillModalOpen(true)}>
                  <Plus size={16} /> Add Your First Bill Reminder
                </button>
              </div>
            ) : (
              <div className="et-bills-grid">
                {bills.map((bill) => {
                  const isPaid = bill.status === 'PAID';
                  return (
                    <div key={bill.id} className={`et-bill-card glass-panel ${isPaid ? 'bill-paid' : ''}`}>
                      <div className="et-bill-card-top">
                        <div className="et-bill-icon-badge">
                          {bill.name.toLowerCase().includes('electr') ? (
                            <Zap size={20} color="#f59e0b" />
                          ) : bill.name.toLowerCase().includes('wifi') || bill.name.toLowerCase().includes('internet') ? (
                            <Wifi size={20} color="#0284c7" />
                          ) : bill.name.toLowerCase().includes('rent') ? (
                            <Landmark size={20} color="#10b981" />
                          ) : (
                            <CreditCard size={20} color="#ec4899" />
                          )}
                        </div>
                        <div className="et-bill-meta">
                          <h3 className="et-bill-name">{bill.name}</h3>
                          <span className="et-bill-category-pill">{bill.category_name || 'Utilities'}</span>
                        </div>
                        <div className="et-bill-status-badge">
                          {isPaid ? (
                            <span className="et-badge et-badge-success">✓ Paid</span>
                          ) : (
                            <span className="et-badge et-badge-warning">Due {bill.due_date}</span>
                          )}
                        </div>
                      </div>

                      <div className="et-bill-body">
                        <div className="et-bill-amount-row">
                          <span className="et-bill-amount-label">Payable:</span>
                          <span className="et-bill-amount-val">₹{Number(bill.amount).toLocaleString('en-IN')}</span>
                        </div>
                        <div className="et-bill-account-note">
                          Account: {bill.account_name || 'HDFC Salary Bank'} ({bill.frequency || 'Monthly'})
                        </div>
                      </div>

                      <div className="et-bill-actions">
                        {!isPaid ? (
                          <button
                            type="button"
                            className="et-btn et-btn-primary et-bill-pay-btn"
                            onClick={() => handleMarkBillPaid(bill)}
                          >
                            <CheckCircle2 size={14} /> Mark as Paid
                          </button>
                        ) : (
                          <span className="et-bill-paid-note">Logged to ledger</span>
                        )}
                        <button
                          type="button"
                          className="btn-icon et-btn-danger"
                          title="Delete Bill Reminder"
                          onClick={() => handleDeleteBill(bill.id)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* 12. RECURRING EXPENSES VIEW */}
        {activeTab === 'recurring' && (
          <div className="et-recurring-page animate-fade-in">
            <div className="et-card-header-row">
              <div>
                <h2>Automated & Recurring Schedules</h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.84rem', margin: '2px 0 0 0' }}>
                  Manage scheduled debits, salary credits, SIP investments, and repeat expenses.
                </p>
              </div>
              <button className="et-btn et-btn-primary" onClick={() => setRecurringModalOpen(true)}>
                <Plus size={16} /> New Recurring Schedule
              </button>
            </div>

            {recurringRules.length === 0 ? (
              <div className="et-recurring-guide-card glass-panel">
                <div className="et-recurring-icon-bubble">
                  <RefreshCw size={32} color="#059669" />
                </div>
                <h3 className="et-guide-title">Automate Recurring Cash Flows</h3>
                <p className="et-guide-desc">
                  Set recurring schedules for income (monthly salary, consulting retainers) or repeated outflows (house rent, gym, insurance, SIPs).
                </p>
                <div className="et-guide-fields-grid">
                  <div className="et-guide-field-item">
                    <div className="et-guide-step-num">1</div>
                    <div className="et-guide-field-content">
                      <strong>Title & Merchant</strong>
                      <span>e.g., "Monthly Salary", "Apartment Rent", "Mutual Fund SIP"</span>
                    </div>
                  </div>
                  <div className="et-guide-field-item">
                    <div className="et-guide-step-num">2</div>
                    <div className="et-guide-field-content">
                      <strong>Type & Amount (₹)</strong>
                      <span>Specify whether it is an Income credit or Expense debit</span>
                    </div>
                  </div>
                  <div className="et-guide-field-item">
                    <div className="et-guide-step-num">3</div>
                    <div className="et-guide-field-content">
                      <strong>Cadence / Frequency</strong>
                      <span>Monthly, Weekly, Daily, or Yearly execution</span>
                    </div>
                  </div>
                  <div className="et-guide-field-item">
                    <div className="et-guide-step-num">4</div>
                    <div className="et-guide-field-content">
                      <strong>Account & Category</strong>
                      <span>Mapped directly to your linked ledger accounts</span>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="et-btn et-btn-primary et-create-budget-btn"
                  onClick={() => setRecurringModalOpen(true)}
                >
                  <Plus size={16} /> Set Up Recurring Schedule
                </button>
              </div>
            ) : (
              <div className="et-recurring-grid">
                {recurringRules.map((rule) => {
                  const isInc = rule.type === 'INCOME';
                  return (
                    <div key={rule.id} className="et-recurring-card glass-panel">
                      <div className="et-recurring-head">
                        <div>
                          <span className={`et-badge ${isInc ? 'et-badge-success' : 'et-badge-danger'}`}>
                            {rule.type} • {rule.frequency}
                          </span>
                          <h3 className="et-recurring-name" style={{ marginTop: '6px' }}>
                            {rule.description || rule.merchant || 'Scheduled Transaction'}
                          </h3>
                        </div>
                        <button
                          type="button"
                          className="btn-icon et-btn-danger"
                          title="Delete Schedule"
                          onClick={() => handleDeleteRecurring(rule.id)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>

                      <div className="et-recurring-figures">
                        <span className="et-recurring-amt">₹{Number(rule.amount).toLocaleString('en-IN')}</span>
                        <span className="et-recurring-date">Next: {rule.next_occurrence || 'Auto-scheduled'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* 13. ANALYTICS VIEW */}
        {activeTab === 'analytics' && (
          <div className="et-analytics-page animate-fade-in">
            <div className="et-card-header-row">
              <div>
                <h2>Financial Analytics & Spending Velocity</h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.84rem', margin: '2px 0 0 0' }}>
                  Deep mathematical breakdown of cashflow, daily burn rate, and category distribution.
                </p>
              </div>
            </div>

            {/* Top Analytics Metrics */}
            <div className="et-analytics-kpi-grid">
              <div className="et-stat-card glass-panel">
                <span className="et-stat-label">Net Savings Rate</span>
                <div className="et-stat-val" style={{ color: '#10b981' }}>
                  {dashboardData?.metrics?.savingsRate || 0}%
                </div>
                <span className="et-stat-sub">
                  Net saved: ₹{(dashboardData?.metrics?.netSavings || 0).toLocaleString('en-IN')}
                </span>
              </div>
              <div className="et-stat-card glass-panel">
                <span className="et-stat-label">Average Daily Burn Rate</span>
                <div className="et-stat-val" style={{ color: '#38bdf8' }}>
                  ₹{Math.round((dashboardData?.metrics?.totalExpenses || 0) / (new Date().getDate() || 1)).toLocaleString('en-IN')} / day
                </div>
                <span className="et-stat-sub">Across active month</span>
              </div>
              <div className="et-stat-card glass-panel">
                <span className="et-stat-label">Top Expense Category</span>
                <div className="et-stat-val" style={{ color: '#f43f5e', fontSize: '1.25rem' }}>
                  {dashboardData?.categoryBreakdown?.[0]?.name || 'Food'}
                </div>
                <span className="et-stat-sub">
                  ₹{(dashboardData?.categoryBreakdown?.[0]?.amount || 7040).toLocaleString('en-IN')} ({dashboardData?.categoryBreakdown?.[0]?.percentage || 83.7}%)
                </span>
              </div>
              <div className="et-stat-card glass-panel">
                <span className="et-stat-label">Total Monthly Outflow</span>
                <div className="et-stat-val" style={{ color: '#ef4444' }}>
                  ₹{(dashboardData?.metrics?.totalExpenses || 8410).toLocaleString('en-IN')}
                </div>
                <span className="et-stat-sub">Transfers excluded</span>
              </div>
            </div>

            {/* Category Breakdown & Account Balances */}
            <div className="et-analytics-columns-grid">
              {/* Category Breakdown */}
              <div className="et-card glass-panel">
                <h3 className="et-card-title">Category Outflow Breakdown</h3>
                <div className="et-analytics-cat-list">
                  {(dashboardData?.categoryBreakdown || [
                    { name: 'Food & Dining', amount: 7040, percentage: 83.7, color: '#10b981' },
                    { name: 'Transport & Fuel', amount: 1300, percentage: 15.5, color: '#0284c7' },
                    { name: 'Other Essentials', amount: 70, percentage: 0.8, color: '#f59e0b' },
                  ]).map((cat, idx) => (
                    <div key={idx} className="et-cat-row-item">
                      <div className="et-cat-row-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span className="et-cat-dot" style={{ background: cat.color || '#10b981' }} />
                          <span className="et-cat-name">{cat.name}</span>
                        </div>
                        <div className="et-cat-numbers">
                          <strong>₹{Number(cat.amount).toLocaleString('en-IN')}</strong>
                          <span className="et-cat-pct">({cat.percentage}%)</span>
                        </div>
                      </div>
                      <div className="et-progress-bar-bg">
                        <div
                          className="et-progress-bar-fill"
                          style={{ width: `${Math.min(100, cat.percentage)}%`, backgroundColor: cat.color || '#10b981' }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Account Distribution & Payment Methods */}
              <div className="et-card glass-panel">
                <h3 className="et-card-title">Account Distribution & Net Worth</h3>
                <div className="et-analytics-acc-list">
                  {accounts.map((acc) => (
                    <div key={acc.id} className="et-acc-dist-row">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div className="et-acc-icon-mini" style={{ background: `${acc.color || '#059669'}20`, color: acc.color || '#059669' }}>
                          <Landmark size={14} />
                        </div>
                        <div>
                          <div className="et-acc-name-text">{acc.name}</div>
                          <span className="et-acc-type-sub">{acc.type}</span>
                        </div>
                      </div>
                      <div className="et-acc-balance-text">
                        ₹{Number(acc.current_balance).toLocaleString('en-IN')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 14. AI FINANCIAL ASSISTANT VIEW */}
        {activeTab === 'ai' && (
          <div className="et-ai-chat-layout">
            <div className="et-ai-chat-header">
              <div className="et-ai-bot-badge">
                <Bot size={20} color="#059669" />
                <div>
                  <strong>CipherVault AI Financial Assistant</strong>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    Scoped strictly to your authorized database • No arbitrary SQL execution
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Prompt Chips */}
            <div className="et-ai-prompt-chips">
              {[
                'How much did I spend on food?',
                'Where am I spending most?',
                'How much did I save this month?',
                'Which category increased?',
                'Where can I reduce spending?',
                'How much are my subscriptions costing?',
              ].map((chip, idx) => (
                <button key={idx} className="et-chip-btn" onClick={() => handleSendChat(chip)}>
                  {chip}
                </button>
              ))}
            </div>

            {/* Chat History */}
            <div className="et-ai-chat-history">
              {chatMessages.map((msg, i) => (
                <div key={i} className={`et-chat-bubble et-chat-${msg.role}`}>
                  <div className="et-chat-avatar">
                    {msg.role === 'assistant' ? <Bot size={18} /> : <User size={18} />}
                  </div>
                  <div className="et-chat-body">
                    <div style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</div>
                    {msg.tools && msg.tools.length > 0 && (
                      <div className="et-tool-call-chips">
                        <span>Verified data tools:</span>
                        {msg.tools.map((t, tidx) => (
                          <span key={tidx} className="et-tool-pill">{t}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div className="et-chat-bubble et-chat-assistant">
                  <div className="et-chat-avatar"><Bot size={18} /></div>
                  <div className="et-chat-body"><RefreshCcw size={16} className="et-spin" /> Querying verified database tools...</div>
                </div>
              )}
            </div>

            {/* Chat Input Bar */}
            <form onSubmit={(e) => { e.preventDefault(); handleSendChat(); }} className="et-ai-chat-input-row">
              <input
                type="text"
                placeholder="Ask about your finances (e.g. Where am I spending the most?)..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                className="et-ai-chat-input"
                disabled={chatLoading}
              />
              <button type="submit" className="et-btn et-btn-primary" disabled={chatLoading || !chatInput.trim()}>
                <Send size={16} />
              </button>
            </form>
          </div>
        )}

        {/* 15. SPENDING INSIGHTS VIEW (Authentic iOS Design) */}
        {activeTab === 'insights' && (
          <div className="et-insights-page animate-fade-in">
            <div className="et-card-header-row">
              <div>
                <div className="ios-badge-row" style={{ marginBottom: '6px' }}>
                  <span className="et-badge et-badge-info">
                    <Sparkles size={11} /> iOS Financial Intelligence
                  </span>
                  <span className="et-badge et-badge-default">MoM Variance Engine</span>
                </div>
                <h2>Spending Insights & Month-over-Month Variance</h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.84rem', margin: '2px 0 0 0' }}>
                  Real-time variance detection, category spending shifts, and AI financial recommendations.
                </p>
              </div>
            </div>

            {insightsData && (
              <div className="et-insights-layout-grid">
                {/* Hero Month-over-Month Variance Card */}
                <div className="et-insights-hero-card glass-panel">
                  <div className="et-insights-hero-header">
                    <div>
                      <span className="et-insights-eyebrow">MONTH-OVER-MONTH SPENDING VARIANCE</span>
                      <div className="et-insights-delta-display">
                        <span className={`et-variance-badge ${insightsData.monthComparison.isHigher ? 'variance-higher' : 'variance-lower'}`}>
                          {insightsData.monthComparison.isHigher ? <TrendingUp size={22} /> : <TrendingDown size={22} />}
                          {insightsData.monthComparison.isHigher ? '+' : ''}{insightsData.monthComparison.percentChange}%
                        </span>
                      </div>
                    </div>
                    <div className="et-insights-neutral-note">
                      ✓ Transfers between own accounts neutralized
                    </div>
                  </div>

                  <p className="et-insights-summary-text">
                    Current month spending is <strong>₹{insightsData.monthComparison.currentMonthTotal.toLocaleString('en-IN')}</strong> compared to last month's baseline of <strong>₹{insightsData.monthComparison.previousMonthTotal.toLocaleString('en-IN')}</strong>.
                  </p>

                  {/* 3 iOS Metric Comparison Tiles */}
                  <div className="et-variance-tiles-grid">
                    <div className="et-variance-tile">
                      <span className="et-tile-label">This Month Spending</span>
                      <div className="et-tile-val" style={{ color: '#ef4444' }}>
                        ₹{insightsData.monthComparison.currentMonthTotal.toLocaleString('en-IN')}
                      </div>
                      <span className="et-tile-sub">Active cycle outflow</span>
                    </div>
                    <div className="et-variance-tile">
                      <span className="et-tile-label">Prior Month Baseline</span>
                      <div className="et-tile-val" style={{ color: '#94a3b8' }}>
                        ₹{insightsData.monthComparison.previousMonthTotal.toLocaleString('en-IN')}
                      </div>
                      <span className="et-tile-sub">Comparison period</span>
                    </div>
                    <div className="et-variance-tile">
                      <span className="et-tile-label">Net Outflow Shift</span>
                      <div className="et-tile-val" style={{ color: insightsData.monthComparison.isHigher ? '#f59e0b' : '#10b981' }}>
                        {insightsData.monthComparison.isHigher ? '+' : '-'}₹{Math.abs(insightsData.monthComparison.currentMonthTotal - insightsData.monthComparison.previousMonthTotal).toLocaleString('en-IN')}
                      </div>
                      <span className="et-tile-sub">{insightsData.monthComparison.percentChange}% variance</span>
                    </div>
                  </div>
                </div>

                {/* Category Spending Shifts (Apple Inset Grouped) */}
                <div className="et-card glass-panel">
                  <div className="et-card-header-row" style={{ marginBottom: '14px' }}>
                    <h3 className="et-card-title">Category Spending Shifts</h3>
                    <span className="et-badge et-badge-default">Ranked by Volume</span>
                  </div>

                  <div className="et-cat-shifts-list">
                    {(insightsData.categoryShifts || []).map((sh, idx) => {
                      const totalCurrent = insightsData.monthComparison.currentMonthTotal || 1;
                      const catPct = Math.min(100, Math.round((Math.max(0, sh.change) / totalCurrent) * 100));
                      return (
                        <div key={idx} className="et-shift-card-row">
                          <div className="et-shift-row-top">
                            <div className="et-shift-cat-info">
                              <span className="et-shift-cat-name">{sh.category}</span>
                              <span className="et-shift-cat-share">{catPct}% of total spending</span>
                            </div>
                            <div className="et-shift-badge-wrap">
                              <span className={`et-shift-amt-badge ${sh.change > 0 ? 'shift-up' : 'shift-down'}`}>
                                {sh.change > 0 ? '+' : ''}₹{sh.change.toLocaleString('en-IN')} ({sh.percentChange}%)
                              </span>
                            </div>
                          </div>

                          <div className="et-progress-bar-bg" style={{ marginTop: '8px' }}>
                            <div
                              className="et-progress-bar-fill"
                              style={{
                                width: `${catPct}%`,
                                backgroundColor: sh.change > 5000 ? '#ef4444' : sh.change > 1000 ? '#f59e0b' : '#059669',
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Smart Financial Recommendations */}
                <div className="et-card glass-panel">
                  <div className="et-card-header-row" style={{ marginBottom: '14px' }}>
                    <h3 className="et-card-title">AI Financial Optimization Advice</h3>
                    <span className="et-badge et-badge-success">Actionable Tips</span>
                  </div>

                  <div className="et-insights-advice-list">
                    <div className="et-advice-item">
                      <div className="et-advice-icon-wrap" style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' }}>
                        <AlertTriangle size={18} />
                      </div>
                      <div className="et-advice-text">
                        <strong>Concentrated Spending in Food & Dining</strong>
                        <p>Food accounts for over 80% of your expenses this month. Setting a ₹6,000 category budget will notify you at 80% before overspending.</p>
                      </div>
                    </div>

                    <div className="et-advice-item">
                      <div className="et-advice-icon-wrap" style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8' }}>
                        <Landmark size={18} />
                      </div>
                      <div className="et-advice-text">
                        <strong>Diversify Payment Methods</strong>
                        <p>All expenses are currently funded through HDFC Salary Bank. You have 3 other active payment accounts with available balance.</p>
                      </div>
                    </div>

                    <div className="et-advice-item">
                      <div className="et-advice-icon-wrap" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}>
                        <ShieldCheck size={18} />
                      </div>
                      <div className="et-advice-text">
                        <strong>Zero-Knowledge Security Protection</strong>
                        <p>All financial statements, receipts, and natural language voice queries are encrypted on-device. Zero advertising tracking.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 16. IMPORT VIEW */}
        {activeTab === 'import' && (
          <div className="et-import-page et-card">
            <h2>Import Bank Statements, CSV & Receipts</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
              Upload your HDFC, ICICI, SBI, Axis, or UPI statement in CSV or Excel format. Duplicates will be detected automatically.
            </p>
            <div className="et-dropzone" onClick={() => document.getElementById('et-file-input').click()}>
              <UploadCloud size={40} color="#059669" />
              <h3>Click or drag & drop CSV or Excel bank statement</h3>
              <p>Supports .csv, .xlsx, .xls</p>
              <input
                id="et-file-input"
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  showToast('Uploading and analyzing statement with deduplication engine...');
                  // simulated import
                  setTimeout(() => {
                    showToast('5 transactions detected. 1 duplicate flagged.', 'info');
                  }, 1200);
                }}
              />
            </div>
          </div>
        )}

        {/* 17. REPORTS VIEW */}
        {activeTab === 'reports' && (
          <div className="et-reports-page et-card">
            <div className="et-reports-header">
              <h2>Financial Statements & Export</h2>
              <p className="text-secondary" style={{ fontSize: '0.82rem', margin: '4px 0 0 0' }}>
                Download, export, and print encrypted statements and category ledgers.
              </p>
            </div>

            <div className="et-reports-actions">
              <button
                className="et-btn et-btn-primary"
                onClick={() => {
                  if (!transactions.length) {
                    showToast('No transactions to export', 'info');
                    return;
                  }
                  const headers = ['Date', 'Type', 'Category', 'Subcategory', 'Amount', 'Currency', 'Account', 'Merchant', 'Description', 'Method'];
                  const rows = transactions.map(t => [
                    t.transaction_date,
                    t.type,
                    `"${t.category_name || ''}"`,
                    `"${t.subcategory_name || ''}"`,
                    t.amount,
                    t.currency || 'INR',
                    `"${t.account_name || ''}"`,
                    `"${t.merchant || ''}"`,
                    `"${(t.description || '').replace(/"/g, '""')}"`,
                    t.payment_method || ''
                  ]);
                  const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
                  const encodedUri = encodeURI(csvContent);
                  const link = document.createElement('a');
                  link.setAttribute('href', encodedUri);
                  link.setAttribute('download', `CipherVault_Statement_${new Date().toISOString().split('T')[0]}.csv`);
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  showToast('Exported statement to CSV successfully');
                }}
              >
                <Download size={15} />
                <span>Export to CSV</span>
              </button>

              <button
                className="et-btn et-btn-secondary"
                onClick={() => {
                  if (!transactions.length) {
                    showToast('No transactions to export', 'info');
                    return;
                  }
                  const headers = ['Date', 'Type', 'Category', 'Amount', 'Account', 'Description'];
                  const rows = transactions.map(t => [
                    t.transaction_date,
                    t.type,
                    `"${t.category_name || ''}"`,
                    t.amount,
                    `"${t.account_name || ''}"`,
                    `"${(t.description || '').replace(/"/g, '""')}"`
                  ]);
                  const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
                  const encodedUri = encodeURI(csvContent);
                  const link = document.createElement('a');
                  link.setAttribute('href', encodedUri);
                  link.setAttribute('download', `CipherVault_Excel_Export_${new Date().toISOString().split('T')[0]}.csv`);
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  showToast('Exported Excel statement successfully');
                }}
              >
                <FileSpreadsheet size={15} />
                <span>Export to Excel</span>
              </button>

              <button className="et-btn et-btn-outline" onClick={() => window.print()}>
                <FileText size={15} />
                <span>Print / Save PDF</span>
              </button>
            </div>

            {/* Financial Summary Preview */}
            <div className="et-report-preview-box">
              <div className="et-report-preview-header">
                <div>
                  <h3 style={{ fontSize: '0.92rem', margin: 0, fontWeight: 600 }}>Period Statement Summary</h3>
                  <span className="text-secondary" style={{ fontSize: '0.74rem' }}>
                    Generated on {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
                <span className="et-badge et-badge-income" style={{ fontSize: '0.7rem' }}>
                  Audited & Encrypted
                </span>
              </div>

              <div className="et-report-metrics-grid">
                <div className="et-report-metric-item">
                  <span className="et-report-metric-lbl">Total Inflow</span>
                  <strong className="et-report-metric-val" style={{ color: '#059669' }}>
                    +₹{(dashboardData?.metrics?.totalIncome || 0).toLocaleString('en-IN')}
                  </strong>
                </div>
                <div className="et-report-metric-item">
                  <span className="et-report-metric-lbl">Total Outflow</span>
                  <strong className="et-report-metric-val" style={{ color: '#ef4444' }}>
                    -₹{(dashboardData?.metrics?.totalExpenses ?? dashboardData?.metrics?.totalExpense ?? 0).toLocaleString('en-IN')}
                  </strong>
                </div>
                <div className="et-report-metric-item">
                  <span className="et-report-metric-lbl">Net Savings</span>
                  <strong className="et-report-metric-val" style={{ color: '#2563eb' }}>
                    ₹{(dashboardData?.metrics?.netSavings ?? ((dashboardData?.metrics?.totalIncome || 0) - (dashboardData?.metrics?.totalExpenses ?? dashboardData?.metrics?.totalExpense ?? 0))).toLocaleString('en-IN')}
                  </strong>
                </div>
                <div className="et-report-metric-item">
                  <span className="et-report-metric-lbl">Recorded Transactions</span>
                  <strong className="et-report-metric-val">{totalTxCount || transactions.length}</strong>
                </div>
              </div>

              {/* Recent Ledger Entries Preview */}
              {transactions && transactions.length > 0 && (
                <div className="et-report-ledger-preview">
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', margin: '14px 0 8px 0' }}>
                    Recent Statement Entries
                  </div>
                  <div className="et-report-ledger-list">
                    {transactions.slice(0, 5).map((tx) => (
                      <div key={tx.id || Math.random()} className="et-report-ledger-row">
                        <div className="et-report-ledger-left">
                          <span className="et-report-ledger-desc">{tx.description || tx.category_name || 'Transaction'}</span>
                          <span className="et-report-ledger-sub">
                            {tx.transaction_date ? new Date(tx.transaction_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ''} • {tx.account_name || 'Main Wallet'}
                          </span>
                        </div>
                        <span className={`et-report-ledger-amt ${tx.type === 'INCOME' ? 'et-amt-income' : 'et-amt-expense'}`}>
                          {tx.type === 'INCOME' ? '+' : '-'}₹{Number(tx.amount || 0).toLocaleString('en-IN')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 18. SETTINGS VIEW (Gemini API Key & Privacy) */}
        {activeTab === 'settings' && (
          <div className="et-settings-page et-card">
            <h2>AI Configuration & Encryption Settings</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
              Configure your Google Gemini API key. Keys are encrypted at rest with AES-256-GCM and never exposed to the frontend or saved in logs.
            </p>

            <div className="et-settings-form">
              <div className="et-setting-field">
                <label>Google Gemini API Key</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="password"
                    placeholder={aiSettings?.has_api_key ? '••••••••••••••••••••••••••••••••' : 'Enter your Gemini API key'}
                    className="et-input"
                    id="et-gemini-key-input"
                  />
                  <button
                    className="et-btn et-btn-primary"
                    onClick={async () => {
                      const input = document.getElementById('et-gemini-key-input');
                      const val = input.value.trim();
                      if (!val) {
                        showToast('Please enter an API key', 'error');
                        return;
                      }
                      try {
                        await updateAiSettings({ gemini_api_key: val });
                        showToast('Gemini API key encrypted and saved securely!');
                        input.value = '';
                        loadAllData();
                      } catch (err) {
                        showToast('Failed to save API key', 'error');
                      }
                    }}
                  >
                    Save Key
                  </button>
                  {aiSettings?.has_api_key && (
                    <button
                      className="et-btn et-btn-danger"
                      onClick={async () => {
                        if (window.confirm('Remove saved Gemini API key?')) {
                          await removeAiKey();
                          showToast('API key removed');
                          loadAllData();
                        }
                      }}
                    >
                      Remove Key
                    </button>
                  )}
                </div>
                <span className="et-field-hint">
                  {aiSettings?.has_api_key
                    ? '✓ Key securely encrypted in database with AES-256-GCM.'
                    : 'No personal key configured. Using server fallback if available.'}
                </span>
              </div>

              <div className="et-setting-field">
                <label>AI Preferred Response Language</label>
                <select
                  className="et-select"
                  value={aiSettings?.preferred_language || 'Auto Detect'}
                  onChange={async (e) => {
                    const lang = e.target.value;
                    await updateAiSettings({ preferred_language: lang });
                    showToast(`Preferred language set to ${lang}`);
                    loadAllData();
                  }}
                >
                  <option value="Auto Detect">Auto Detect (English/Hinglish)</option>
                  <option value="English">English</option>
                  <option value="Hindi">Hindi</option>
                  <option value="Gujarati">Gujarati</option>
                  <option value="Hinglish">Hinglish</option>
                </select>
              </div>

              <div className="et-setting-toggles">
                <label className="et-toggle-row">
                  <input
                    type="checkbox"
                    checked={aiSettings?.ai_enabled !== false}
                    onChange={async (e) => {
                      await updateAiSettings({ ai_enabled: e.target.checked });
                      loadAllData();
                    }}
                  />
                  <span>Enable AI Features (If disabled, manual expense tracking continues normally)</span>
                </label>
                <label className="et-toggle-row">
                  <input
                    type="checkbox"
                    checked={aiSettings?.image_analysis_enabled !== false}
                    onChange={async (e) => {
                      await updateAiSettings({ image_analysis_enabled: e.target.checked });
                      loadAllData();
                    }}
                  />
                  <span>Enable Screenshot Recognition (GPay, PhonePe, Paytm, Bank screenshots)</span>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── MODAL: AI CONFIRMATION (CRITICAL SAFETY GATE) ─── */}
      {/* ─── DRAWER: AI CONFIRMATION (CRITICAL SAFETY GATE) ─── */}
      {aiConfirmModalOpen && aiCandidate && (
        <div className="card-drawer-wrapper" role="presentation">
          <div className="card-drawer-overlay animate-fade-in" onClick={() => setAiConfirmModalOpen(false)} aria-hidden="true" />
          <aside className="card-drawer et-drawer animate-slide-left" role="dialog" aria-modal="true" aria-label="AI Extraction Preview">
            <div className="card-drawer-header et-drawer-header">
              <div className="card-drawer-header-left">
                <div className="card-drawer-icon-badge" style={{ background: 'rgba(5, 150, 105, 0.15)', color: '#059669' }}>
                  <Sparkles size={18} />
                </div>
                <div className="card-drawer-titles">
                  <h2 className="card-drawer-title">AI Extraction Preview</h2>
                  <p className="card-drawer-subtitle">Review detected expense before saving</p>
                </div>
              </div>
              <button type="button" className="btn-icon" onClick={() => setAiConfirmModalOpen(false)} aria-label="Close drawer"><X size={18} /></button>
            </div>

            <div className="card-drawer-body et-drawer-body">
              {/* Risk Engine Warning Banner */}
              {aiRisk && aiRisk.risk_level !== 'LOW' && (
                <div className={`et-risk-banner et-risk-${aiRisk.risk_level.toLowerCase()}`}>
                  <AlertTriangle size={16} />
                  <div>
                    <strong>{aiRisk.alerts[0]?.title || 'Spending Alert'}:</strong> {aiRisk.alerts[0]?.message}
                    <div style={{ fontSize: '11px', marginTop: '2px' }}>Advisory only — your purchase is never blocked.</div>
                  </div>
                </div>
              )}

              {/* Duplicate Warning */}
              {aiDuplicate?.is_duplicate && (
                <div className="et-duplicate-banner">
                  <AlertTriangle size={16} color="#d97706" />
                  <div>
                    <strong>Possible Duplicate Detected:</strong> {aiDuplicate.reason}
                    <div style={{ display: 'flex', gap: '16px', marginTop: '6px' }}>
                      <label style={{ fontSize: '0.8rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="dupChoice"
                          checked={duplicateChoice === 'keep'}
                          onChange={() => setDuplicateChoice('keep')}
                        /> Keep Both
                      </label>
                      <label style={{ fontSize: '0.8rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="dupChoice"
                          checked={duplicateChoice === 'ignore'}
                          onChange={() => setDuplicateChoice('ignore')}
                        /> Ignore / Skip
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Structured Details */}
              <div className="et-ai-preview-grid">
                <div className="et-preview-field">
                  <label>Amount</label>
                  <div className="et-preview-val">₹{aiCandidate.amount}</div>
                </div>
                <div className="et-preview-field">
                  <label>Type</label>
                  <div className="et-preview-val">{aiCandidate.type}</div>
                </div>
                <div className="et-preview-field">
                  <label>Category</label>
                  <div className="et-preview-val">{aiCandidate.category} {aiCandidate.subcategory ? `(${aiCandidate.subcategory})` : ''}</div>
                </div>
                <div className="et-preview-field">
                  <label>Merchant / Details</label>
                  <div className="et-preview-val">{aiCandidate.description || aiCandidate.merchant}</div>
                </div>
                <div className="et-preview-field">
                  <label>Date</label>
                  <div className="et-preview-val">{formatTxDate(aiCandidate.transaction_date)}</div>
                </div>
                <div className="et-preview-field">
                  <label>Payment Method</label>
                  <div className="et-preview-val">{aiCandidate.payment_method || 'Cash / UPI'}</div>
                </div>
              </div>
            </div>

            <div className="card-drawer-footer et-drawer-footer">
              <button
                type="button"
                className="et-btn et-btn-outline"
                onClick={() => {
                  setAiConfirmModalOpen(false);
                  openAddTransaction(aiCandidate.type);
                }}
              >
                Edit
              </button>
              <button type="button" className="et-btn et-btn-secondary" onClick={() => setAiConfirmModalOpen(false)}>
                Cancel
              </button>
              <button type="button" className="et-btn et-btn-primary" onClick={handleConfirmAiExpense}>
                Add Expense
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* ─── DRAWER: MANUAL ADD / EDIT TRANSACTION ─── */}
      {txModalOpen && (
        <div className="card-drawer-wrapper" role="presentation">
          <div className="card-drawer-overlay animate-fade-in" onClick={() => setTxModalOpen(false)} aria-hidden="true" />
          <aside className="card-drawer et-drawer animate-slide-left" role="dialog" aria-modal="true" aria-label={editingTx ? 'Edit Transaction' : 'Add New Transaction'}>
            <div className="card-drawer-header et-drawer-header">
              <div className="card-drawer-header-left">
                <div className="card-drawer-icon-badge" style={{ background: 'rgba(99, 102, 241, 0.15)', color: '#6366f1' }}>
                  {txForm.type === 'INCOME' ? <TrendingUp size={18} /> : <TrendingDown size={18} />}
                </div>
                <div className="card-drawer-titles">
                  <h2 className="card-drawer-title">{editingTx ? 'Edit Transaction' : 'Add New Transaction'}</h2>
                  <p className="card-drawer-subtitle">{editingTx ? 'Update transaction details' : 'Record a new income, expense, or transfer'}</p>
                </div>
              </div>
              <button type="button" className="btn-icon" onClick={() => setTxModalOpen(false)} aria-label="Close drawer"><X size={18} /></button>
            </div>

            <form onSubmit={handleSaveTransaction} id="tx-drawer-form" style={{ display: 'contents' }}>
              <div className="card-drawer-body et-drawer-body">
                <div className="et-form-grid">
                  <div className="et-form-field">
                    <label>Type</label>
                    <select
                      value={txForm.type}
                      onChange={(e) => setTxForm({ ...txForm, type: e.target.value })}
                      className="et-select"
                    >
                      <option value="EXPENSE">Expense (Debit)</option>
                      <option value="INCOME">Income (Credit)</option>
                      <option value="REFUND">Refund</option>
                      <option value="INVESTMENT">Investment</option>
                    </select>
                  </div>

                  <div className="et-form-field">
                    <label>Amount (₹)*</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={txForm.amount}
                      onChange={(e) => setTxForm({ ...txForm, amount: e.target.value })}
                      className="et-input"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="et-form-field">
                    <label>Category</label>
                    <select
                      value={txForm.category_id}
                      onChange={(e) => setTxForm({ ...txForm, category_id: e.target.value })}
                      className="et-select"
                    >
                      <option value="">Select Category</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="et-form-field">
                    <label>Account</label>
                    <select
                      value={txForm.account_id}
                      onChange={(e) => setTxForm({ ...txForm, account_id: e.target.value })}
                      className="et-select"
                    >
                      <option value="">Select Account</option>
                      {accounts.map((a) => (
                        <option key={a.id} value={a.id}>{a.name} (₹{Number(a.current_balance).toLocaleString('en-IN')})</option>
                      ))}
                    </select>
                  </div>

                  <div className="et-form-field">
                    <label>Merchant / Payee</label>
                    <input
                      type="text"
                      value={txForm.merchant}
                      onChange={(e) => setTxForm({ ...txForm, merchant: e.target.value })}
                      className="et-input"
                      placeholder="e.g. Swiggy, Amazon, Shell"
                    />
                  </div>

                  <div className="et-form-field">
                    <label>Description</label>
                    <input
                      type="text"
                      value={txForm.description}
                      onChange={(e) => setTxForm({ ...txForm, description: e.target.value })}
                      className="et-input"
                      placeholder="e.g. Lunch with team, monthly groceries"
                    />
                  </div>

                  <div className="et-form-field">
                    <label>Transaction Date</label>
                    <input
                      type="date"
                      value={txForm.transaction_date}
                      onChange={(e) => setTxForm({ ...txForm, transaction_date: e.target.value })}
                      className="et-input"
                    />
                  </div>

                  <div className="et-form-field">
                    <label>Payment Method</label>
                    <select
                      value={txForm.payment_method}
                      onChange={(e) => setTxForm({ ...txForm, payment_method: e.target.value })}
                      className="et-select"
                    >
                      <option value="UPI">UPI (GPay / PhonePe / Paytm)</option>
                      <option value="Cash">Cash</option>
                      <option value="Credit Card">Credit Card</option>
                      <option value="Debit Card">Debit Card</option>
                      <option value="Net Banking">Net Banking</option>
                      <option value="Wallet">Wallet</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="card-drawer-footer et-drawer-footer">
                <button type="button" className="et-btn et-btn-secondary" onClick={() => setTxModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="et-btn et-btn-primary">
                  {editingTx ? 'Update Transaction' : 'Add Transaction'}
                </button>
              </div>
            </form>
          </aside>
        </div>
      )}

      {/* ─── DRAWER: TRANSFER ─── */}
      {transferModalOpen && (
        <div className="card-drawer-wrapper" role="presentation">
          <div className="card-drawer-overlay animate-fade-in" onClick={() => setTransferModalOpen(false)} aria-hidden="true" />
          <aside className="card-drawer et-drawer animate-slide-left" role="dialog" aria-modal="true" aria-label="Transfer Between Accounts">
            <div className="card-drawer-header et-drawer-header">
              <div className="card-drawer-header-left">
                <div className="card-drawer-icon-badge" style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' }}>
                  <Repeat size={18} />
                </div>
                <div className="card-drawer-titles">
                  <h2 className="card-drawer-title">Transfer Funds</h2>
                  <p className="card-drawer-subtitle">Transfer between accounts (Net ₹0 expense)</p>
                </div>
              </div>
              <button type="button" className="btn-icon" onClick={() => setTransferModalOpen(false)} aria-label="Close drawer"><X size={18} /></button>
            </div>

            <form onSubmit={handleSaveTransfer} id="transfer-drawer-form" style={{ display: 'contents' }}>
              <div className="card-drawer-body et-drawer-body">
                <div className="et-form-grid">
                  <div className="et-form-field">
                    <label>From Account (Debit)</label>
                    <select
                      value={transferForm.from_account_id}
                      onChange={(e) => setTransferForm({ ...transferForm, from_account_id: e.target.value })}
                      className="et-select"
                      required
                    >
                      <option value="">Select Source Account</option>
                      {accounts.map((a) => (
                        <option key={a.id} value={a.id}>{a.name} (Bal: ₹{Number(a.current_balance).toLocaleString('en-IN')})</option>
                      ))}
                    </select>
                  </div>

                  <div className="et-form-field">
                    <label>To Account (Credit)</label>
                    <select
                      value={transferForm.to_account_id}
                      onChange={(e) => setTransferForm({ ...transferForm, to_account_id: e.target.value })}
                      className="et-select"
                      required
                    >
                      <option value="">Select Destination Account</option>
                      {accounts.map((a) => (
                        <option key={a.id} value={a.id}>{a.name} (Bal: ₹{Number(a.current_balance).toLocaleString('en-IN')})</option>
                      ))}
                    </select>
                  </div>

                  <div className="et-form-field">
                    <label>Amount (₹)*</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={transferForm.amount}
                      onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })}
                      className="et-input"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="et-form-field">
                    <label>Transfer Date</label>
                    <input
                      type="date"
                      value={transferForm.transaction_date}
                      onChange={(e) => setTransferForm({ ...transferForm, transaction_date: e.target.value })}
                      className="et-input"
                    />
                  </div>
                </div>
              </div>

              <div className="card-drawer-footer et-drawer-footer">
                <button type="button" className="et-btn et-btn-secondary" onClick={() => setTransferModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="et-btn et-btn-primary">
                  Execute Transfer
                </button>
              </div>
            </form>
          </aside>
        </div>
      )}

      {/* ─── DRAWER: RECEIPT SCANNER ─── */}
      {receiptModalOpen && (
        <div className="card-drawer-wrapper" role="presentation">
          <div className="card-drawer-overlay animate-fade-in" onClick={() => setReceiptModalOpen(false)} aria-hidden="true" />
          <aside className="card-drawer et-drawer animate-slide-left" role="dialog" aria-modal="true" aria-label="Scan Receipt or Payment Screenshot">
            <div className="card-drawer-header et-drawer-header">
              <div className="card-drawer-header-left">
                <div className="card-drawer-icon-badge" style={{ background: 'rgba(5, 150, 105, 0.15)', color: '#059669' }}>
                  <Upload size={18} />
                </div>
                <div className="card-drawer-titles">
                  <h2 className="card-drawer-title">Scan Receipt / Screenshot</h2>
                  <p className="card-drawer-subtitle">Instant AI parsing from UPI or bill receipt</p>
                </div>
              </div>
              <button type="button" className="btn-icon" onClick={() => setReceiptModalOpen(false)} aria-label="Close drawer"><X size={18} /></button>
            </div>

            <div className="card-drawer-body et-drawer-body">
              <div className="et-receipt-upload-box">
                <div className="et-dropzone" onClick={() => document.getElementById('et-receipt-file-input').click()}>
                  <Sparkles size={36} color="#059669" />
                  <h4>Upload GPay, PhonePe, Paytm, Bank Screenshot, or Physical Receipt</h4>
                  <p>Sensitive card/bank account numbers are automatically masked</p>
                  <input
                    id="et-receipt-file-input"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      showToast('Analyzing screenshot with Gemini Multimodal AI...');
                      const reader = new FileReader();
                      reader.onload = async () => {
                        try {
                          const base64 = reader.result;
                          const res = await parseImage(base64, file.type);
                          setReceiptModalOpen(false);
                          setAiCandidate(res.data.extracted);
                          setAiRisk(res.data.risk);
                          setAiDuplicate(res.data.duplicate);
                          setDuplicateChoice('keep');
                          setAiConfirmModalOpen(true);
                        } catch (err) {
                          showToast(err.response?.data?.message || 'Failed to extract receipt data', 'error');
                        }
                      };
                      reader.readAsDataURL(file);
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="card-drawer-footer et-drawer-footer">
              <button type="button" className="et-btn et-btn-secondary" onClick={() => setReceiptModalOpen(false)}>
                Cancel
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* ─── DRAWER: ADD ACCOUNT ─── */}
      {accountModalOpen && (
        <div className="card-drawer-wrapper" role="presentation">
          <div className="card-drawer-overlay animate-fade-in" onClick={() => setAccountModalOpen(false)} aria-hidden="true" />
          <aside className="card-drawer et-drawer animate-slide-left" role="dialog" aria-modal="true" aria-label="Add Payment Account / Wallet">
            <div className="card-drawer-header et-drawer-header">
              <div className="card-drawer-header-left">
                <div className="card-drawer-icon-badge" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}>
                  <Landmark size={18} />
                </div>
                <div className="card-drawer-titles">
                  <h2 className="card-drawer-title">Add Payment Account</h2>
                  <p className="card-drawer-subtitle">Connect a bank account, cash wallet, or UPI ID</p>
                </div>
              </div>
              <button type="button" className="btn-icon" onClick={() => setAccountModalOpen(false)} aria-label="Close drawer"><X size={18} /></button>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target;
                const payload = {
                  name: form.name.value.trim(),
                  type: form.type.value,
                  opening_balance: Number(form.opening_balance.value) || 0,
                  institution_name: form.institution_name.value.trim() || undefined,
                  account_number_last4: form.account_number_last4.value.trim() || undefined,
                  color: form.color.value || '#10b981',
                  is_default: form.is_default.checked,
                  notes: form.notes.value.trim() || undefined,
                };
                try {
                  await createAccount(payload);
                  showToast('Payment account created successfully');
                  setAccountModalOpen(false);
                  loadAllData();
                } catch (err) {
                  showToast(err.response?.data?.message || 'Failed to create account', 'error');
                }
              }}
              id="account-drawer-form"
              style={{ display: 'contents' }}
            >
              <div className="card-drawer-body et-drawer-body">
                <div className="et-form-grid">
                  <div className="et-form-field">
                    <label>Account Name*</label>
                    <input name="name" type="text" required placeholder="e.g. HDFC Salary Account, Cash Wallet" className="et-input" />
                  </div>
                  <div className="et-form-field">
                    <label>Account Type*</label>
                    <select name="type" className="et-select" defaultValue="Bank Account">
                      <option value="Bank Account">Bank Account (Savings / Current)</option>
                      <option value="Cash">Cash / Physical Wallet</option>
                      <option value="UPI">UPI / Digital Wallet</option>
                      <option value="Credit Card">Credit Card</option>
                      <option value="Debit Card">Debit Card</option>
                      <option value="Investment">Investment / Demat</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="et-form-field">
                    <label>Opening Balance (₹)</label>
                    <input name="opening_balance" type="number" step="0.01" defaultValue="0" className="et-input" placeholder="0.00" />
                  </div>
                  <div className="et-form-field">
                    <label>Bank / Institution Name</label>
                    <input name="institution_name" type="text" placeholder="e.g. HDFC Bank, SBI, Paytm" className="et-input" />
                  </div>
                  <div className="et-form-field">
                    <label>Last 4 Digits of Account/Card (Optional)</label>
                    <input name="account_number_last4" type="text" maxLength={4} placeholder="e.g. 4321" className="et-input" />
                  </div>
                  <div className="et-form-field">
                    <label>Card / Theme Color</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input name="color" type="color" defaultValue="#10b981" style={{ width: '42px', height: '38px', padding: '2px', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer' }} />
                      <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>Accent color on dashboard</span>
                    </div>
                  </div>
                  <div className="et-form-field" style={{ gridColumn: '1 / -1' }}>
                    <label>Notes / Remarks</label>
                    <input name="notes" type="text" placeholder="e.g. Primary salary account" className="et-input" />
                  </div>
                  <div className="et-form-field" style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input id="acc-default-chk" name="is_default" type="checkbox" style={{ width: '16px', height: '16px', accentColor: '#059669', cursor: 'pointer' }} />
                    <label htmlFor="acc-default-chk" style={{ margin: 0, cursor: 'pointer' }}>Set as default account for new expenses</label>
                  </div>
                </div>
              </div>
              <div className="card-drawer-footer et-drawer-footer">
                <button type="button" className="et-btn et-btn-secondary" onClick={() => setAccountModalOpen(false)}>Cancel</button>
                <button type="submit" className="et-btn et-btn-primary">Create Account</button>
              </div>
            </form>
          </aside>
        </div>
      )}

      {/* ─── DRAWER: ADD CATEGORY ─── */}
      {categoryModalOpen && (
        <div className="card-drawer-wrapper" role="presentation">
          <div className="card-drawer-overlay animate-fade-in" onClick={() => setCategoryModalOpen(false)} aria-hidden="true" />
          <aside className="card-drawer et-drawer animate-slide-left" role="dialog" aria-modal="true" aria-label="Add Custom Category">
            <div className="card-drawer-header et-drawer-header">
              <div className="card-drawer-header-left">
                <div className="card-drawer-icon-badge" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' }}>
                  <Folder size={18} />
                </div>
                <div className="card-drawer-titles">
                  <h2 className="card-drawer-title">Add Category</h2>
                  <p className="card-drawer-subtitle">Create custom category for tracking expenses</p>
                </div>
              </div>
              <button type="button" className="btn-icon" onClick={() => setCategoryModalOpen(false)} aria-label="Close drawer"><X size={18} /></button>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target;
                const payload = {
                  name: form.name.value.trim(),
                  type: form.type.value,
                  color: form.color.value || '#10b981',
                };
                try {
                  await createCategory(payload);
                  showToast('Category created successfully');
                  setCategoryModalOpen(false);
                  loadAllData();
                } catch (err) {
                  showToast(err.response?.data?.message || 'Failed to create category', 'error');
                }
              }}
              id="category-drawer-form"
              style={{ display: 'contents' }}
            >
              <div className="card-drawer-body et-drawer-body">
                <div className="et-form-grid">
                  <div className="et-form-field">
                    <label>Category Name*</label>
                    <input name="name" type="text" required placeholder="e.g. Fitness & Gym, Gadgets" className="et-input" />
                  </div>
                  <div className="et-form-field">
                    <label>Category Type*</label>
                    <select name="type" className="et-select" defaultValue="EXPENSE">
                      <option value="EXPENSE">Expense Category</option>
                      <option value="INCOME">Income Category</option>
                      <option value="BOTH">Both (Expense & Income)</option>
                    </select>
                  </div>
                  <div className="et-form-field" style={{ gridColumn: '1 / -1' }}>
                    <label>Color Accent</label>
                    <input name="color" type="color" defaultValue="#10b981" style={{ width: '42px', height: '38px', padding: '2px', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer' }} />
                  </div>
                </div>
              </div>
              <div className="card-drawer-footer et-drawer-footer">
                <button type="button" className="et-btn et-btn-secondary" onClick={() => setCategoryModalOpen(false)}>Cancel</button>
                <button type="submit" className="et-btn et-btn-primary">Save Category</button>
              </div>
            </form>
          </aside>
        </div>
      )}

      {/* ─── DRAWER: CREATE GOAL ─── */}
      {goalModalOpen && (
        <div className="card-drawer-wrapper" role="presentation">
          <div className="card-drawer-overlay animate-fade-in" onClick={() => setGoalModalOpen(false)} aria-hidden="true" />
          <aside className="card-drawer et-drawer animate-slide-left" role="dialog" aria-modal="true" aria-label="Create Savings Goal">
            <div className="card-drawer-header et-drawer-header">
              <div className="card-drawer-header-left">
                <div className="card-drawer-icon-badge" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}>
                  <Target size={18} />
                </div>
                <div className="card-drawer-titles">
                  <h2 className="card-drawer-title">Create Savings Goal</h2>
                  <p className="card-drawer-subtitle">Set milestone and target timeline</p>
                </div>
              </div>
              <button type="button" className="btn-icon" onClick={() => setGoalModalOpen(false)} aria-label="Close drawer"><X size={18} /></button>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target;
                const payload = {
                  name: form.name.value.trim(),
                  target_amount: Number(form.target_amount.value),
                  current_amount: Number(form.current_amount.value) || 0,
                  target_date: form.target_date.value || undefined,
                };
                try {
                  await createGoal(payload);
                  showToast('Savings goal created successfully');
                  setGoalModalOpen(false);
                  loadAllData();
                } catch (err) {
                  showToast(err.response?.data?.message || 'Failed to create goal', 'error');
                }
              }}
              id="goal-drawer-form"
              style={{ display: 'contents' }}
            >
              <div className="card-drawer-body et-drawer-body">
                <div className="et-form-grid">
                  <div className="et-form-field">
                    <label>Goal Name*</label>
                    <input name="name" type="text" required placeholder="e.g. Emergency Fund, New Laptop" className="et-input" />
                  </div>
                  <div className="et-form-field">
                    <label>Target Amount (₹)*</label>
                    <input name="target_amount" type="number" required placeholder="50000" className="et-input" />
                  </div>
                  <div className="et-form-field">
                    <label>Initial Saved Amount (₹)</label>
                    <input name="current_amount" type="number" defaultValue="0" placeholder="0" className="et-input" />
                  </div>
                  <div className="et-form-field">
                    <label>Target Date</label>
                    <input name="target_date" type="date" className="et-input" />
                  </div>
                </div>
              </div>
              <div className="card-drawer-footer et-drawer-footer">
                <button type="button" className="et-btn et-btn-secondary" onClick={() => setGoalModalOpen(false)}>Cancel</button>
                <button type="submit" className="et-btn et-btn-primary">Save Goal</button>
              </div>
            </form>
          </aside>
        </div>
      )}

      {/* ─── DRAWER: ADD SUBSCRIPTION ─── */}
      {subscriptionModalOpen && (
        <div className="card-drawer-wrapper" role="presentation">
          <div className="card-drawer-overlay animate-fade-in" onClick={() => setSubscriptionModalOpen(false)} aria-hidden="true" />
          <aside className="card-drawer et-drawer animate-slide-left" role="dialog" aria-modal="true" aria-label="Add Recurring Subscription">
            <div className="card-drawer-header et-drawer-header">
              <div className="card-drawer-header-left">
                <div className="card-drawer-icon-badge" style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' }}>
                  <CalendarClock size={18} />
                </div>
                <div className="card-drawer-titles">
                  <h2 className="card-drawer-title">Add Subscription</h2>
                  <p className="card-drawer-subtitle">Track recurring SaaS, OTT, or memberships</p>
                </div>
              </div>
              <button type="button" className="btn-icon" onClick={() => setSubscriptionModalOpen(false)} aria-label="Close drawer"><X size={18} /></button>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target;
                const payload = {
                  name: form.name.value.trim(),
                  amount: Number(form.amount.value),
                  billing_cycle: form.billing_cycle.value,
                  account_id: form.account_id.value || undefined,
                  category_id: form.category_id.value || undefined,
                  next_payment_date: form.next_payment_date.value || undefined,
                };
                try {
                  await createSubscription(payload);
                  showToast('Subscription added successfully');
                  setSubscriptionModalOpen(false);
                  loadAllData();
                } catch (err) {
                  showToast(err.response?.data?.message || 'Failed to add subscription', 'error');
                }
              }}
              id="sub-drawer-form"
              style={{ display: 'contents' }}
            >
              <div className="card-drawer-body et-drawer-body">
                <div className="et-form-grid">
                  <div className="et-form-field">
                    <label>Service / Subscription Name*</label>
                    <input name="name" type="text" required placeholder="e.g. Netflix, Spotify, Amazon Prime" className="et-input" />
                  </div>
                  <div className="et-form-field">
                    <label>Recurring Amount (₹)*</label>
                    <input name="amount" type="number" step="0.01" required placeholder="649" className="et-input" />
                  </div>
                  <div className="et-form-field">
                    <label>Billing Cycle*</label>
                    <select name="billing_cycle" className="et-select" defaultValue="MONTHLY">
                      <option value="MONTHLY">Monthly</option>
                      <option value="YEARLY">Yearly / Annual</option>
                      <option value="QUARTERLY">Quarterly</option>
                      <option value="WEEKLY">Weekly</option>
                    </select>
                  </div>
                  <div className="et-form-field">
                    <label>Payment Account</label>
                    <select name="account_id" className="et-select">
                      <option value="">Select Account</option>
                      {accounts.map((a) => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="et-form-field">
                    <label>Category</label>
                    <select name="category_id" className="et-select">
                      <option value="">Select Category</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="et-form-field">
                    <label>Next Billing Date</label>
                    <input name="next_payment_date" type="date" className="et-input" />
                  </div>
                </div>
              </div>
              <div className="card-drawer-footer et-drawer-footer">
                <button type="button" className="et-btn et-btn-secondary" onClick={() => setSubscriptionModalOpen(false)}>Cancel</button>
                <button type="submit" className="et-btn et-btn-primary">Add Subscription</button>
              </div>
            </form>
          </aside>
        </div>
      )}

      {/* ─── DRAWER: CREATE BUDGET ─── */}
      {budgetModalOpen && (
        <div className="card-drawer-wrapper" role="presentation">
          <div className="card-drawer-overlay animate-fade-in" onClick={() => setBudgetModalOpen(false)} aria-hidden="true" />
          <aside className="card-drawer et-drawer animate-slide-left" role="dialog" aria-modal="true" aria-label="Create Category Budget">
            <div className="card-drawer-header et-drawer-header">
              <div className="card-drawer-header-left">
                <div className="card-drawer-icon-badge" style={{ background: 'rgba(147, 51, 234, 0.15)', color: '#9333ea' }}>
                  <PieChart size={18} />
                </div>
                <div className="card-drawer-titles">
                  <h2 className="card-drawer-title">Create Budget</h2>
                  <p className="card-drawer-subtitle">Set spending limits and alert threshold</p>
                </div>
              </div>
              <button type="button" className="btn-icon" onClick={() => setBudgetModalOpen(false)} aria-label="Close drawer"><X size={18} /></button>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const name = e.target.name.value;
                const amount = Number(e.target.amount.value);
                const category_id = e.target.category_id.value || null;
                const threshold_percent = Number(e.target.threshold.value);
                try {
                  await createBudget({ name, amount, category_id, threshold_percent, period: 'MONTHLY' });
                  showToast('Budget created successfully');
                  setBudgetModalOpen(false);
                  loadAllData();
                } catch (err) {
                  showToast(err.response?.data?.message || 'Failed to create budget', 'error');
                }
              }}
              id="budget-drawer-form"
              style={{ display: 'contents' }}
            >
              <div className="card-drawer-body et-drawer-body">
                <div className="et-form-grid">
                  <div className="et-form-field">
                    <label>Budget Name*</label>
                    <input name="name" type="text" required placeholder="e.g. Monthly Food & Dining" className="et-input" />
                  </div>
                  <div className="et-form-field">
                    <label>Monthly Limit (₹)*</label>
                    <input name="amount" type="number" required placeholder="6000" className="et-input" />
                  </div>
                  <div className="et-form-field">
                    <label>Category (Optional for overall)</label>
                    <select name="category_id" className="et-select">
                      <option value="">Overall Total Budget</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="et-form-field">
                    <label>Alert Threshold (%)</label>
                    <input name="threshold" type="number" defaultValue="80" min="50" max="100" className="et-input" />
                  </div>
                </div>
              </div>
              <div className="card-drawer-footer et-drawer-footer">
                <button type="button" className="et-btn et-btn-secondary" onClick={() => setBudgetModalOpen(false)}>Cancel</button>
                <button type="submit" className="et-btn et-btn-primary">Save Budget</button>
              </div>
            </form>
          </aside>
        </div>
      )}

      {/* ─── DRAWER: ADD BILL REMINDER ─── */}
      {billModalOpen && (
        <div className="card-drawer-wrapper" role="presentation">
          <div className="card-drawer-overlay animate-fade-in" onClick={() => setBillModalOpen(false)} aria-hidden="true" />
          <aside className="card-drawer et-drawer animate-slide-left" role="dialog" aria-modal="true" aria-label="Add Bill Reminder">
            <div className="card-drawer-header et-drawer-header">
              <div className="card-drawer-header-left">
                <div className="card-drawer-icon-badge" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' }}>
                  <BellRing size={18} />
                </div>
                <div className="card-drawer-titles">
                  <h2 className="card-drawer-title">Add Bill Reminder</h2>
                  <p className="card-drawer-subtitle">Track due dates for electricity, rent, wifi, and credit cards</p>
                </div>
              </div>
              <button type="button" className="btn-icon" onClick={() => setBillModalOpen(false)} aria-label="Close drawer"><X size={18} /></button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.target;
                const newBill = {
                  id: 'b-' + Date.now(),
                  name: form.name.value.trim(),
                  amount: Number(form.amount.value),
                  due_date: form.due_date.value,
                  category_name: form.category_name.value || 'Bills & Utilities',
                  account_name: form.account_name.value || accounts[0]?.name || 'HDFC Salary Bank',
                  frequency: form.frequency.value || 'MONTHLY',
                  status: 'UPCOMING',
                };
                const updatedBills = [newBill, ...bills];
                saveBillsToStorage(updatedBills);
                showToast(`Bill reminder for "${newBill.name}" scheduled!`);
                setBillModalOpen(false);
              }}
              id="bill-drawer-form"
              style={{ display: 'contents' }}
            >
              <div className="card-drawer-body et-drawer-body">
                <div className="et-form-grid">
                  <div className="et-form-field">
                    <label>Bill / Service Name*</label>
                    <input name="name" type="text" required placeholder="e.g. Torrent Power Electricity, JioFiber, House Rent" className="et-input" />
                  </div>
                  <div className="et-form-field">
                    <label>Payable Amount (₹)*</label>
                    <input name="amount" type="number" step="0.01" required placeholder="1850" className="et-input" />
                  </div>
                  <div className="et-form-field">
                    <label>Due Date*</label>
                    <input
                      name="due_date"
                      type="date"
                      required
                      defaultValue={new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0]}
                      className="et-input"
                    />
                  </div>
                  <div className="et-form-field">
                    <label>Category</label>
                    <select name="category_name" className="et-select" defaultValue="Bills & Utilities">
                      <option value="Bills & Utilities">Bills & Utilities</option>
                      <option value="Electricity">Electricity</option>
                      <option value="Internet / WiFi">Internet / WiFi</option>
                      <option value="Rent / Housing">Rent / Housing</option>
                      <option value="Credit Card Due">Credit Card Due</option>
                      <option value="Mobile Recharge">Mobile Recharge</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="et-form-field">
                    <label>Payment Account</label>
                    <select name="account_name" className="et-select">
                      {accounts.map((a) => (
                        <option key={a.id} value={a.name}>{a.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="et-form-field">
                    <label>Cycle / Frequency</label>
                    <select name="frequency" className="et-select" defaultValue="MONTHLY">
                      <option value="MONTHLY">Monthly</option>
                      <option value="ONE_TIME">One-Time Due</option>
                      <option value="QUARTERLY">Quarterly</option>
                      <option value="YEARLY">Yearly</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="card-drawer-footer et-drawer-footer">
                <button type="button" className="et-btn et-btn-secondary" onClick={() => setBillModalOpen(false)}>Cancel</button>
                <button type="submit" className="et-btn et-btn-primary">Schedule Reminder</button>
              </div>
            </form>
          </aside>
        </div>
      )}

      {/* ─── DRAWER: NEW RECURRING SCHEDULE ─── */}
      {recurringModalOpen && (
        <div className="card-drawer-wrapper" role="presentation">
          <div className="card-drawer-overlay animate-fade-in" onClick={() => setRecurringModalOpen(false)} aria-hidden="true" />
          <aside className="card-drawer et-drawer animate-slide-left" role="dialog" aria-modal="true" aria-label="New Recurring Schedule">
            <div className="card-drawer-header et-drawer-header">
              <div className="card-drawer-header-left">
                <div className="card-drawer-icon-badge" style={{ background: 'rgba(5, 150, 105, 0.15)', color: '#059669' }}>
                  <RefreshCw size={18} />
                </div>
                <div className="card-drawer-titles">
                  <h2 className="card-drawer-title">New Recurring Schedule</h2>
                  <p className="card-drawer-subtitle">Automate repeat salary credits, SIP investments, or rent debits</p>
                </div>
              </div>
              <button type="button" className="btn-icon" onClick={() => setRecurringModalOpen(false)} aria-label="Close drawer"><X size={18} /></button>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target;
                const payload = {
                  description: form.description.value.trim(),
                  merchant: form.description.value.trim(),
                  type: form.type.value,
                  amount: Number(form.amount.value),
                  frequency: form.frequency.value,
                  category_id: form.category_id.value || undefined,
                  account_id: form.account_id.value || undefined,
                  start_date: form.start_date.value || new Date().toISOString().split('T')[0],
                };
                try {
                  await createRecurring(payload);
                  showToast('Recurring transaction schedule saved!');
                  setRecurringModalOpen(false);
                  loadAllData();
                } catch (err) {
                  showToast(err.response?.data?.message || 'Failed to create recurring rule', 'error');
                }
              }}
              id="recurring-drawer-form"
              style={{ display: 'contents' }}
            >
              <div className="card-drawer-body et-drawer-body">
                <div className="et-form-grid">
                  <div className="et-form-field">
                    <label>Description / Merchant*</label>
                    <input name="description" type="text" required placeholder="e.g. Monthly Salary, House Rent, Mutual Fund SIP" className="et-input" />
                  </div>
                  <div className="et-form-field">
                    <label>Transaction Type*</label>
                    <select name="type" className="et-select" defaultValue="EXPENSE">
                      <option value="EXPENSE">Expense (Debit outflow)</option>
                      <option value="INCOME">Income (Credit inflow)</option>
                    </select>
                  </div>
                  <div className="et-form-field">
                    <label>Recurring Amount (₹)*</label>
                    <input name="amount" type="number" step="0.01" required placeholder="5000" className="et-input" />
                  </div>
                  <div className="et-form-field">
                    <label>Cadence / Frequency*</label>
                    <select name="frequency" className="et-select" defaultValue="MONTHLY">
                      <option value="MONTHLY">Monthly</option>
                      <option value="WEEKLY">Weekly</option>
                      <option value="DAILY">Daily</option>
                      <option value="YEARLY">Yearly</option>
                    </select>
                  </div>
                  <div className="et-form-field">
                    <label>Linked Account</label>
                    <select name="account_id" className="et-select">
                      <option value="">Select Account</option>
                      {accounts.map((a) => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="et-form-field">
                    <label>Category</label>
                    <select name="category_id" className="et-select">
                      <option value="">Select Category</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="et-form-field">
                    <label>Start / First Execution Date</label>
                    <input name="start_date" type="date" defaultValue={new Date().toISOString().split('T')[0]} className="et-input" />
                  </div>
                </div>
              </div>
              <div className="card-drawer-footer et-drawer-footer">
                <button type="button" className="et-btn et-btn-secondary" onClick={() => setRecurringModalOpen(false)}>Cancel</button>
                <button type="submit" className="et-btn et-btn-primary">Save Recurring Schedule</button>
              </div>
            </form>
          </aside>
        </div>
      )}

      {/* ─── FLOATING ACTION BUTTON (BOTTOM RIGHT) ─── */}
      <VoiceExpenseFab
        onOpenAddExpense={() => openAddTransaction('EXPENSE')}
        onTransactionCreated={() => {
          loadAllData();
          loadTransactions();
          showToast('Expense added successfully via Voice AI!');
        }}
        categories={categories}
        accounts={accounts}
      />
      </div>
    </MainLayout>
  );
}
